// LanesCanvasPlane — the lanes plane drawn on one canvas instead of one
// element per cell.
//
// See docs/projects/lanes-canvas-plane.md. Renders a laneScene
// complete: the sticky strip stack (turn ruler with beats and occupancy, the
// token density strip with its cumulative line, the content-joins strip), lane
// strips, merged spans, marks, influence flow lines, join cell pins, follow
// rings, and a spliced sub-agent SUB-SCENE drawn into the same canvas. The dock
// stays DOM; the rail and the strip headers are drawn here, in the scope tree's
// typography and row chrome (the rail section of drawScene), so the plane is
// one surface that scrolls and occludes in lockstep.
//
// Scroll architecture, and the reason this is not just "the same tree on a
// canvas": the canvas is VIEWPORT-SIZED and never resizes with the content. A
// transparent scroller sits on top of it holding a single spacer element sized
// to the plane, so native scrolling, momentum, keyboard paging and real
// scrollbars all keep working while the drawn surface stays one screen. The DOM
// plane's 11,724px-wide element (at 1:1 on a 560-turn session) is gone, and
// with it the whole class of overflow bugs that element caused — the phantom
// overlay bar, the edge-spill clamping, the fit-mode rounding remainder that
// minted a horizontal scrollbar.
//
// Three pinning behaviours share one draw: prims with `pin: "y"` hold their
// vertical place (the strip stack), prims with `sticky: true` hold their
// horizontal place (the rail, an inset box whose child keeps its own clock),
// and everything else travels with both axes. A SUB-SCENE draws with pinning
// disabled — inside a box that does not scroll, a pinned strip would float free
// of the rows it labels.
import { memo, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import HoverTooltip from "./HoverTooltip.jsx";
import ScrollArea from "./ScrollArea.jsx";
import { useThemeVersion } from "../lib/theme.js";
import { observeSettledSize } from "../lib/settledResize.js";
import { truncateToWidth } from "../lib/canvasText.js";
import { getDotTilePattern } from "../lib/renderers.js";
import { STIPPLE_SIZE_COMPACT, STIPPLE_DOT_RADIUS_COMPACT } from "../lib/stipple.js";
import { guideChip, guideTargets, relatedChips } from "../lib/traceLaneLayout.js";
import {
  RAIL_W, ROW_H, CHIP_H, SYS_PIN_W, STRIP_CTRL_W, INSET_PAD_X, INSET_HEAD_LINES, MARK_TOKEN, SPAN_SEG_MIX, TIER_TOKEN, tierStroke, tierOpacity,
  RAIL_SPARK_H,
  hitTestScene, edgeChipsAt, insetPanRange, moveFocus, describeFocus, focusRect,
  firstAtOrAfter, CULL_SLACK, turnWindowFor, windowCovers, guidePrims, episodeBracket,
  systemBandShift, pinnedBandInset,
} from "../lib/laneScene.js";

// The plane's geometry contract. The DOM plane published its geometry through
// data-* anchors that siblings measured; a canvas plane has no elements to
// anchor to, so the SCENE is what it publishes instead -- rows, cells, strips
// and their exact boxes. Keyed by the plane's scrolling element, so a caller
// that can find the plane can read what it drew.
const PLANE_SCENES = new WeakMap();
export function planeScene(el) {
  return (el && PLANE_SCENES.get(el)) || null;
}

// The chips the last FRAME discovered -- edge pointers and related-row
// pointers. They are a function of the viewport rather than of the scene, so
// they cannot live in it; published the same way for the same reason.
const PLANE_CHIPS = new WeakMap();
export function planeChips(el) {
  return (el && PLANE_CHIPS.get(el)) || [];
}

// The strip-mark guide the last frame drew, if any, published the same way.
const PLANE_GUIDES = new WeakMap();
export function planeGuide(el) {
  return (el && PLANE_GUIDES.get(el)) || null;
}

// The virtual trigger a pointer stands in for: a mark's own size, so the house
// tooltip places against something the same shape it would have in DOM.
const MARK_ANCHOR = 14;

// The house sweep's period, matching the CSS the DOM strips use.
const SWEEP_MS = 2200;
const reducedMotion = () => typeof window !== "undefined"
  && typeof window.matchMedia === "function"
  && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// The hover reading, as its own component. The plane must not call `tooltipOf`
// inline: the elements it returns would be OWNED by the plane, so the card's
// whole subtree renders inside the plane's own render, and anything that
// subtree settles lands while the plane is still rendering. Rendering it here
// gives the card its own fiber, which is what the DOM plane's HoverTooltip did.
const PlaneTip = memo(function PlaneTip({ tooltipOf, hit, clientX, clientY }) {
  const body = tooltipOf(hit);
  // The house tooltip, driven from a virtual anchor. The plane has no per-mark
  // element to hang one on, but the treatment and -- more to the point -- the
  // edge correction are the reader's, not this surface's: a hand-rolled popover
  // had neither, and ran off the bottom of the viewport near the last rows.
  const anchor = useMemo(() => (body ? {
    top: clientY - MARK_ANCHOR / 2, bottom: clientY + MARK_ANCHOR / 2,
    left: clientX - MARK_ANCHOR / 2, right: clientX + MARK_ANCHOR / 2,
    width: MARK_ANCHOR, height: MARK_ANCHOR,
  } : null), [body, clientX, clientY]);
  if (!body) return null;
  return (
    <HoverTooltip
      wide
      maxWidth={560}
      // The rail reads sideways, everything on the axis reads upward -- the
      // same placements the DOM plane asked for.
      placement={hit?.type === "rail" || hit?.type === "striphead" ? "right" : "top"}
      anchorRect={anchor}
      label={body}
    />
  );
});

// Off-screen but present in the accessibility tree.
const SR_ONLY = {
  position: "absolute", width: 1, height: 1, padding: 0, margin: -1,
  overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0,
};

const SANS = "Inter, system-ui, sans-serif";

// The rail is set in the scope tree's language (SidebarNavRuntime's rows, the
// classes in lib/ui.js), so the sidebar and a trace's lanes read as one tree.
// Two voices only: the eyebrow for anything that names a section (the strip
// heads, the group heads) and the entry title for every row a reader opens or
// picks. A third face for the group heads read as a mixture (found live). CSS
// states tracking in em; the canvas takes it as letterSpacing on the context,
// and the text caches key on it.
const RAIL_TITLE_FONT = `500 11px ${SANS}`; // TraceRow's title, the trace listing's row
const EYEBROW_FONT = `11px ${SANS}`; // EYEBROW_CLASS
const EYEBROW_TRACKING = "0.06em";
const RAIL_PAD_X = 8;
// The gutter cell every row carries (the tree's chevron or leaf dash sits in
// it) hugs the rail's left edge, and the label follows after a gap wide enough
// that caret and word do not read as one glyph: the tree's 6px inset, 14px cell
// and 8px gap, at the rail's 12px indent step.
const RAIL_GUTTER_X = 4;
const RAIL_GUTTER_W = 12;
const RAIL_LABEL_GAP = 8;
const RAIL_INDENT = 12;
// The two-line entry (an 11px title over the sparkline) in a 30px row, centred
// as one unit: about five pixels of air above the label's caps, the gutter
// glyph centred on the label, the sparkline band a pixel under the descenders
// from the label's own left edge to the rail's right inset, as the tree's
// does, and four pixels of floor below it.
const RAIL_LABEL_BASELINE = 13;
const RAIL_GLYPH_CY = 9;
const RAIL_SPARK_FLOOR = 4;

// Truncation is memoized because it is the plane's single most expensive draw
// step: truncateToWidth binary-searches measureText, so a rail of ~80 labels
// plus their digests costs well over a thousand measureText calls per frame —
// 15ms of a 16ms budget, measured, dwarfing every rect the plane draws. The
// answer for the same (text, width, font) never changes, and a pan changes none
// of the three, so one cache turns per-frame text measurement into a
// per-layout-change cost.
const CLIP_CACHE = new Map();
const CLIP_CACHE_MAX = 4096;
function clipText(ctx, text, maxW) {
  if (!text) return "";
  // Quantized to whole pixels: a fractional cellW makes maxW wobble by
  // sub-pixel amounts between frames, which would miss the cache every time
  // while never changing the answer.
  const key = `${ctx.font}|${ctx.letterSpacing || ""}|${Math.round(maxW)}|${text}`;
  const hit = CLIP_CACHE.get(key);
  if (hit !== undefined) return hit;
  const out = truncateToWidth(ctx, text, maxW);
  // Plain size cap rather than an LRU: the working set is one plane's labels,
  // and the entries are small.
  if (CLIP_CACHE.size >= CLIP_CACHE_MAX) CLIP_CACHE.clear();
  CLIP_CACHE.set(key, out);
  return out;
}

// Text widths are measured just as often as text is clipped (the rail's digest
// column, the inset head's layout), and with the same stable inputs.
const WIDTH_CACHE = new Map();
function textWidth(ctx, text) {
  if (!text) return 0;
  const key = `${ctx.font}|${ctx.letterSpacing || ""}|${text}`;
  const hit = WIDTH_CACHE.get(key);
  if (hit !== undefined) return hit;
  const w = ctx.measureText(text).width;
  if (WIDTH_CACHE.size >= CLIP_CACHE_MAX) WIDTH_CACHE.clear();
  WIDTH_CACHE.set(key, w);
  return w;
}

// Resolved once per theme version: readTheme walks getComputedStyle per token,
// which was visible in CPU profiles when called per draw (DotGrid's note).
// Read off `el` when given, so a surface that sets its own ground (the
// workspace's) is drawn on it; the page's otherwise.
function readTheme(el = null) {
  const s = getComputedStyle(el || document.documentElement);
  const g = (v, fallback) => s.getPropertyValue(v).trim() || fallback;
  return {
    bg: g("--bg", "#0f0e0c"),
    ink: g("--ink", "#f5f0e8"),
    muted: g("--muted", "rgba(218,208,198,0.82)"),
    mutedSoft: g("--muted-soft", "rgba(218,208,198,0.74)"),
    line: g("--line", "rgba(232,224,215,0.15)"),
    lineSoft: g("--line-soft", "rgba(232,224,215,0.10)"),
    lineStrong: g("--line-strong", "rgba(232,224,215,0.24)"),
    controlBg: g("--control-bg", "rgba(243,237,228,0.055)"),
    green: g("--green", "#a4c582"),
    greenSoft: g("--green-soft", "rgba(164,197,130,0.16)"),
    plotInkSoft: g("--plot-ink-soft", "rgba(243,237,228,0.20)"),
    grayFill: g("--gray-fill", "rgba(243,237,228,0.07)"),
    red: g("--red", "#c0392b"),
    eyebrow: g("--eyebrow", "rgba(218,208,198,0.68)"),
    ok: g("--ok", "#7fc9a3"),
    join: g("--join", "#5db2ff"),
    graph: g("--graph", "#3d5afe"),
    amber: g("--amber", "#b8860b"),
    info: g("--info", "#7fa3c9"),
  };
}

// CSS color -> {r,g,b,a}. The tokens are hex or rgba() literals (checked in
// styles.css), so this stays a two-branch parse rather than a color library:
// the alpha arithmetic below is what color-mix() was doing in the DOM plane,
// and canvas has no cascade to do it for us.
// Every translucent theme token reaches this in one of TWO spellings, and the
// difference is invisible in development. The dev server hands back the
// stylesheet's own `rgba(243, 237, 228, 0.055)`; the production build minifies
// the identical token to `#f3ede40e`. Reading the hex form's alpha as opaque
// multiplied every row fill, every rule and every muted label by ~18 -- so the
// plane looked right on localhost and washed out on every deployed
// environment (found by comparing localhost against staging side by side).
function parseColor(c) {
  const s = String(c).trim();
  if (s.startsWith("#")) {
    const h = s.slice(1);
    // #rgb and #rgba are shorthand for #rrggbb / #rrggbbaa.
    const pairs = h.length <= 4
      ? [...h].map((d) => d + d)
      : [h.slice(0, 2), h.slice(2, 4), h.slice(4, 6), h.slice(6, 8)];
    const [r, g, b, a] = pairs;
    return {
      r: parseInt(r, 16) || 0,
      g: parseInt(g, 16) || 0,
      b: parseInt(b, 16) || 0,
      // A missing 4th pair is opaque; a present one is 0..255 scaled to 0..1.
      a: a ? parseInt(a, 16) / 255 : 1,
    };
  }
  // Legacy `rgba(r, g, b, a)` and the modern `rgb(r g b / a)` space form. The
  // alpha may be a percentage in either.
  const m = s.match(/rgba?\(([^)]+)\)/);
  if (!m) return { r: 128, g: 128, b: 128, a: 1 };
  const p = m[1].split(/[,/\s]+/).filter(Boolean);
  const num = (v, scale) => {
    const n = parseFloat(v);
    if (!Number.isFinite(n)) return 0;
    return String(v).trim().endsWith("%") ? (n / 100) * scale : n;
  };
  return {
    r: num(p[0], 255), g: num(p[1], 255), b: num(p[2], 255),
    a: p.length > 3 ? num(p[3], 1) : 1,
  };
}
const rgba = (c, a = c.a) => `rgba(${Math.round(c.r)},${Math.round(c.g)},${Math.round(c.b)},${a})`;
// color-mix(in srgb, X pct%, transparent)
const alphaMix = (color, pct) => { const c = parseColor(color); return rgba(c, c.a * (pct / 100)); };
// color-mix(in srgb, X pct%, <base>) — composited, not alpha'd, so a tint over
// the rail stays opaque and occludes the plane behind it.
// `color-mix(in srgb, color pct%, base)`, alpha included. The DOM plane mixes
// marks against `--line`, which is TRANSLUCENT; forcing the result opaque made
// every span and badge border far heavier than the count inside it, because the
// border gained the base's lightness at full strength instead of its share.
function overMix(color, pct, base) {
  const c = parseColor(color);
  const b = parseColor(base);
  const t = pct / 100;
  const a = c.a * t + b.a * (1 - t);
  if (a <= 0) return rgba({ r: 0, g: 0, b: 0, a: 0 });
  // Premultiplied, which is what srgb color-mix does.
  const ch = (k) => (c[k] * c.a * t + b[k] * b.a * (1 - t)) / a;
  return rgba({ r: ch("r"), g: ch("g"), b: ch("b"), a });
}

const markColor = (theme, kind) => {
  const token = MARK_TOKEN[kind] || "muted";
  if (token === "noteInk") return alphaMix(theme.muted, 55);
  return theme[token] || theme.muted;
};
// On a workspace board's cards the cells keep one neutral ink whatever ran in
// them: colour there is left to what is pinned on them and what the review
// found, so a write or an error is not one more thing asking to be looked at.
const NEUTRAL_KINDS = new Set(["write", "error", "verified", "readCall"]);

// Row chrome is the sidebar's (rowChromeClass): a gray wash on hover, the
// green-soft wash for the selected row, and the wash enriching toward green
// when the selected row is also the hovered one. The rail adds the 2px bar.
const rowWash = (theme, p) => (p.selected && p.hovered
  ? alphaMix(theme.green, 18)
  : p.selected ? theme.greenSoft : theme.grayFill);

function roundRect(ctx, x, y, w, h, r) {
  const rr = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

function diamond(ctx, x, y, r) {
  ctx.beginPath();
  ctx.moveTo(x, y - r);
  ctx.lineTo(x + r, y);
  ctx.lineTo(x, y + r);
  ctx.lineTo(x - r, y);
  ctx.closePath();
}

// The tree's Chevron (SidebarNavRuntime): a 12px box showing 5x10 of a 16-unit
// viewBox at a 1.2 stroke, which is 3.75x7.5px at a hairline here.
function drawChevron(ctx, cx, cy, open, color) {
  ctx.save();
  ctx.translate(cx, cy);
  if (open) ctx.rotate(Math.PI / 2);
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(-1.9, -3.75);
  ctx.lineTo(1.9, 0);
  ctx.lineTo(-1.9, 3.75);
  ctx.stroke();
  ctx.restore();
}

// The tree's LeafMarker: the short dash a row that opens nothing carries in its
// gutter cell, so leaves and folders share one left edge.
function drawLeafMark(ctx, cx, cy, color) {
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  ctx.lineCap = "round";
  ctx.beginPath();
  const yy = Math.round(cy) + 0.5;
  ctx.moveTo(cx - 2.5, yy);
  ctx.lineTo(cx + 2.5, yy);
  ctx.stroke();
}

// The scope tree's sparkline, as ScopeColumns draws it on canvas: ONE quadratic
// curve through every bin, zero bins clamped to the floor, a bg halo so the
// line reads over the tint, and the row's token share as the ground behind it
// (the tree's share tint). Same geometry, so the sidebar and the rail show one
// figure; the rail's is greyscale and quiet: the line is the Tokens strip's
// running-total ink (the muted ink at 55%) and the share is the plot's soft
// ink turned down, so forty curves under ink labels read as one surface behind
// the labels rather than as a second, brighter one. The line is continuous
// and always visible: splitting it into runs and dropping the quiet stretches
// left strokes that looked clipped at zero, and a separate faint floor under
// them read as sloppy (both found live). Two deliberate differences remain.
//
// The compact stipple under the curve is the tree's, in the line's own quiet
// ink through the shared tile pattern (lib/renderers.js) rather than a per-dot
// loop, since the rail redraws forty rows on every scroll tick. It went away
// once, when the linear scale left a dense row's curve a pixel off the floor
// and the dots under it read as a dotted rule; on the square-root scale the
// curve has height wherever there is activity, so the dots read as fill.
//
// Heights are on a square-root scale against the row's own peak. Linear put a
// dense row's typical bin at a pixel, so the curve hugged the floor and read
// as a baseline (found live). Square root keeps the peak the peak and gives
// the rest of the row a shape.
function traceSparkCurve(ctx, pts) {
  ctx.beginPath();
  ctx.moveTo(pts[0].x, pts[0].y);
  for (let j = 1; j < pts.length; j++) {
    const cx = (pts[j - 1].x + pts[j].x) / 2;
    ctx.quadraticCurveTo(cx, pts[j - 1].y, pts[j].x, pts[j].y);
  }
}
function drawSpark(ctx, theme, bins, x, y, w, h, share = 0) {
  if (share > 0) {
    ctx.fillStyle = alphaMix(theme.plotInkSoft, 65);
    ctx.fillRect(x, y, w * Math.min(1, share), h);
  }
  let smax = 0;
  for (const v of bins) if (v > smax) smax = v;
  if (!(smax > 0) || bins.length < 2) return;
  const pad = 1;
  const usableH = h - pad * 2;
  const baseY = y + h - pad;
  const step = w / (bins.length - 1);
  const rise = (v) => Math.sqrt(v / smax) * usableH;
  const pts = bins.map((v, j) => ({ x: x + j * step, y: baseY - rise(v) }));
  const ink = alphaMix(theme.muted, 55);
  // Half the line's ink, set once. A globalAlpha over an already translucent
  // ink compounded silently to the same number; this says it.
  const stippleInk = alphaMix(theme.muted, 30);
  // The area under the curve, stippled. Zero stretches have no height, so
  // nothing lands on the floor.
  traceSparkCurve(ctx, pts);
  ctx.lineTo(pts[pts.length - 1].x, baseY);
  ctx.lineTo(pts[0].x, baseY);
  ctx.closePath();
  ctx.save();
  ctx.clip();
  const pattern = getDotTilePattern(ctx, {
    color: stippleInk, size: STIPPLE_SIZE_COMPACT, dotX: 2, dotY: 2, dotRadius: STIPPLE_DOT_RADIUS_COMPACT,
  });
  if (pattern) {
    ctx.fillStyle = pattern;
    ctx.fillRect(x, y, w, h);
  } else {
    ctx.fillStyle = stippleInk;
    for (let dy = y; dy < y + h + STIPPLE_SIZE_COMPACT; dy += STIPPLE_SIZE_COMPACT) {
      for (let dx = x; dx < x + w + STIPPLE_SIZE_COMPACT; dx += STIPPLE_SIZE_COMPACT) {
        ctx.beginPath();
        ctx.arc(dx + 2, dy + 2, STIPPLE_DOT_RADIUS_COMPACT, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }
  ctx.restore();
  ctx.save();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  traceSparkCurve(ctx, pts);
  ctx.strokeStyle = theme.bg;
  ctx.lineWidth = 3.4;
  ctx.stroke();
  traceSparkCurve(ctx, pts);
  ctx.strokeStyle = ink;
  // The tree's 1.4px: at the quiet ink the line wants its full weight back.
  ctx.lineWidth = 1.4;
  ctx.stroke();
  ctx.restore();
}

// The join chip, shared by the strip spans and the cell pins: a ring-edged chip
// — quiet fill, crisp near-white edge. A pale wash and a solid bright fill both
// failed live; what makes a small mark read as an OBJECT is distinctness at the
// edge, not luminance.
// The direction a join mark reads from THIS trace's side. An observed mark gets
// no arrow at all -- an echo is not an exchange, and the needle-side arithmetic
// would happily draw a round-trip for one (found live). Exported because the
// grammar is a claim the plane makes, testable without a pixel.
export function joinGlyph({ out, in: inbound, observed }) {
  if (observed) return "◎";
  if (out && inbound) return "⇄";
  if (out) return "→";
  if (inbound) return "←";
  return "";
}

function drawJoinChip(ctx, theme, x, y, { count, out, in: inbound, hue, observed }) {
  const g = joinGlyph({ out, in: inbound, observed });
  const arrow = g ? `${g} ` : "";
  const label = `${arrow}${count}`;
  ctx.font = `500 8px ${SANS}`;
  const w = Math.max(14, textWidth(ctx, label) + 6);
  const h = 12;
  ctx.fillStyle = observed ? overMix(theme.join, 10, theme.bg) : overMix(hue || theme.join, 26, theme.bg);
  roundRect(ctx, x, y, w, h, 6);
  ctx.fill();
  ctx.strokeStyle = observed ? alphaMix(theme.ink, 28) : alphaMix(theme.ink, 62);
  ctx.lineWidth = 1;
  roundRect(ctx, x + 0.5, y + 0.5, w - 1, h - 1, 5.5);
  ctx.stroke();
  ctx.fillStyle = observed ? theme.muted : theme.ink;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, x + w / 2, y + h / 2 + 0.5);
  return w;
}

// The strip's rest-state mark for a lone counterpart: a solid dot, small enough
// to say "one relationship here" without spending a counted chip on it.
function drawJoinDot(ctx, theme, x, y, hue) {
  ctx.beginPath();
  ctx.arc(x + 4, y + 6, 4, 0, Math.PI * 2);
  ctx.fillStyle = overMix(hue || theme.join, 85, theme.bg);
  ctx.fill();
  ctx.strokeStyle = alphaMix(theme.ink, 62);
  ctx.lineWidth = 1;
  ctx.stroke();
  return 8;
}

// --- drawing ----------------------------------------------------------------

/**
 * One scene into one context.
 *
 * `ox`/`oy` place a SUB-SCENE inside its parent's box. `pinned` disables the
 * strip stack's vertical pinning and the rail's horizontal pinning, which is
 * what a sub-scene needs: its box does not scroll, so a pinned child strip
 * would float free of the rows it labels.
 */
function drawScene(ctx, scene, theme, {
  scrollX, scrollY, viewW, viewH, ox = 0, oy = 0,
  pinX = true, pinY = true, railH = null, focus = null, focused = false,
  // Group heads as the sidebar's eyebrows (upper case, tracked), or, on a card
  // that reads as a whole on the workspace board, in their own words.
  upperGroups = true,
  // 0..1 sweep phase for pending strips, or null when motion is not wanted.
  sweep = null,
  // The turn the pointer is over. The episode guides and the hot beat follow it,
  // and they are draw-time facts for the same reason the focus ring is: where a
  // pointer happens to be is not part of what the plane holds.
  pointerTurn = null,
  // The rows a hovered strip mark's cells sit on (guideTargets), for the
  // pointer to the nearest when none is on screen.
  guide = null,
  // Every cell in the one neutral ink (NEUTRAL_KINDS), as a board's cards draw them.
  neutralMarks = false,
}) {
  const colorOf = (kind) => (neutralMarks && NEUTRAL_KINDS.has(kind) ? theme.muted : markColor(theme, kind));
  // Viewport-pinned hit regions the DRAW discovers (the edge chips): the scene
  // cannot hold them because their position is a function of the scroll.
  const X = (x) => x - scrollX + ox;
  const Y = (y) => y - scrollY + oy;
  // A y-pinned prim keeps its plane y as a VIEWPORT y; unpinned inside a box it
  // is just a normal coordinate. Strips always travel with the x axis.
  const SY = (y) => (pinY ? y + oy : Y(y));
  const SX = (x) => X(x);
  // The telemetry band can ride the BOTTOM edge of the viewport instead of its
  // place in the flow -- the mirror of the strip stack, and the same mechanics.
  // It still occupies its flow height, so nothing under it moves; only where it
  // is drawn changes, and only while its flow position is off the bottom.
  const sysMeta = scene.systemMeta;
  const sysPinned = pinY && !!sysMeta?.pinned && sysMeta.h > 0;
  const sysShift = sysPinned ? systemBandShift(sysMeta, { scrollY, oy, viewH }) : 0;
  // A prim of the pinned band draws shifted; everything else is unmoved.
  const RY = (p, y) => (sysPinned && p.system ? y + sysShift : y);
  const L = scene.layers;
  const railW = scene.railW ?? RAIL_W;
  const planeW = railW + scene.width;

  // The visible row band, in plane coordinates. Every row-bound layer is walked
  // as a range from here rather than tested prim by prim.
  // The visible band in THIS scene's own coordinates. `oy` matters: a sub-scene
  // is drawn at an offset with its own scroll at zero, so culling against the
  // raw viewport dropped every row past one viewport-height into the child --
  // exactly the region a reader scrolls the parent down to reach, which is why
  // a mounted inset showed its first rows and then a void.
  const yFrom = scrollY - oy - CULL_SLACK;
  const yTo = scrollY - oy + viewH;

  // --- row backgrounds ---
  const drawRowBg = (p, y) => {
    if (p.t === "runline") {
      // Fills the gap between a spliced lane's own marks, so the two drops into
      // the box below read as one continuous bracket rather than two loose
      // threads hanging off nothing.
      if (y < -20 || y > viewH + 20) return;
      ctx.strokeStyle = alphaMix(theme.muted, 55);
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      const ry = Math.round(y) + 0.5;
      ctx.moveTo(X(p.x0), ry);
      ctx.lineTo(X(p.x1), ry);
      ctx.stroke();
      ctx.setLineDash([]);
      return;
    }
    if (p.t === "sysbar") {
      // The band's bars are the token strip's language, one notch quieter: the
      // telemetry is context for the turns above it, not a reading of its own.
      ctx.fillStyle = p.open ? theme.eyebrow : theme.lineStrong;
      ctx.fillRect(X(p.x), y, p.w, p.h);
      return;
    }
    if (p.selected || p.hovered) {
      ctx.fillStyle = rowWash(theme, p);
      ctx.fillRect(X(0), y, planeW, p.h);
    }
    // Every row, head or lane, is ONE strip across rail and plane: the 1px
    // inset keeps adjacent rows apart, and a mark reads against its lane the
    // whole way. One primitive at one strength: a stronger card on the rail
    // read as a second surface, and a rule above each group on top of the
    // seams was noise that pulled no weight (both found live).
    ctx.fillStyle = alphaMix(theme.controlBg, 45);
    ctx.fillRect(X(0), y + 1, planeW, p.h - 2);
    if (p.session) {
      // A session head is where one trace ends and the next begins on a plane
      // that holds several: its ground is a notch stronger and a hairline
      // sits over it, the one edge between sessions.
      ctx.fillStyle = alphaMix(theme.controlBg, 70);
      ctx.fillRect(X(0), y + 1, planeW, p.h - 2);
      if (!p.session.first) {
        ctx.fillStyle = theme.lineStrong;
        ctx.fillRect(X(0), y, planeW, 1);
      }
    }
    if (p.related) {
      // Outlined rather than filled: a related lane is a pointer to read
      // next, not a claim about what happened in it.
      ctx.strokeStyle = alphaMix(theme.lineStrong, 70);
      ctx.lineWidth = 1;
      ctx.strokeRect(X(0) + 0.5, y + 1.5, planeW - 1, p.h - 3);
    }
  };
  for (let i = firstAtOrAfter(L.rows, yFrom); i < L.rows.length; i++) {
    const p = L.rows[i];
    if (p.y > yTo) break;
    // A pinned band draws in its own pass, over everything it now covers.
    if (sysPinned && p.system) continue;
    drawRowBg(p, Y(p.y));
  }
  // --- clock breaks: a pause the shared axis closed down, quieter than the rows ---
  for (const p of L.bands || []) {
    const x = X(p.x);
    if (x + p.w < 0 || x > viewW) continue;
    ctx.globalAlpha = 0.72;
    ctx.fillStyle = theme.bg;
    ctx.fillRect(x + 1, 0, Math.max(1, p.w - 2), viewH);
    ctx.globalAlpha = 1;
  }

  // --- episode guides, under the marks and over the row strips ---
  const episode = pointerTurn == null ? null : episodeBracket(scene.promptTurns, pointerTurn);
  const guides = pointerTurn == null
    ? L.guides
    : L.guides.concat(guidePrims({ railW,
      promptTurns: scene.promptTurns, hoverTurn: pointerTurn,
      cellW: scene.cellW, width: scene.width, top: scene.headerH,
    }));
  for (const p of guides) {
    const top = Y(p.top);
    const y0 = Math.max(top, 0);
    if (p.t === "episodeband") {
      ctx.fillStyle = alphaMix(theme.eyebrow, 4);
      ctx.fillRect(X(p.x), y0, p.w, viewH - y0);
      continue;
    }
    if (p.t === "episodeedge") {
      ctx.fillStyle = alphaMix(theme.eyebrow, 38);
      ctx.fillRect(X(p.x), y0, 1, viewH - y0);
      continue;
    }
    if (p.t === "crosshair") {
      ctx.fillStyle = alphaMix(theme.ink, 5);
      ctx.fillRect(X(p.x), y0, p.w, viewH - y0);
      continue;
    }
    // The turn range carried in from another reading. Stronger than the hover
    // crosshair (which is transient) and edged on both sides, because this is
    // the answer to "where was I" and has to be findable at a glance.
    if (p.t === "selband") {
      ctx.fillStyle = alphaMix(theme.eyebrow, 16);
      ctx.fillRect(X(p.x), y0, p.w, viewH - y0);
      ctx.fillStyle = alphaMix(theme.eyebrow, 70);
      ctx.fillRect(X(p.x), y0, 1, viewH - y0);
      if (p.w > 2) ctx.fillRect(X(p.x) + p.w - 1, y0, 1, viewH - y0);
      continue;
    }
    // A file seen from two sides: a dashed bracket between the parent's lane
    // and the same lane inside a spliced child. Drawn with the guides because
    // it answers the same question they do -- what else is this.
    if (p.t === "crosslink") {
      const ys = p.ys.map((v) => Y(v));
      ctx.strokeStyle = alphaMix(theme.eyebrow, 65);
      ctx.lineWidth = 1.25;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      for (let i = 1; i < ys.length; i++) {
        ctx.moveTo(X(p.x), ys[i - 1]);
        ctx.bezierCurveTo(X(p.x) - 30, ys[i - 1], X(p.x) - 30, ys[i], X(p.x), ys[i]);
      }
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = alphaMix(theme.eyebrow, 80);
      for (const yy of ys) {
        ctx.beginPath();
        ctx.arc(X(p.x), yy, 2.5, 0, Math.PI * 2);
        ctx.fill();
      }
      continue;
    }
    // The topic run is drawn after the strip stack instead; see below.
  }

  // --- merged spans ---
  for (let i = firstAtOrAfter(L.spans, yFrom); i < L.spans.length; i++) {
    const p = L.spans[i];
    if (p.y > yTo) break;
    const y = Y(p.y);
    const x0 = X(p.x0);
    const x1 = X(p.x1);
    if (x1 < 0 || x0 > viewW || x1 <= x0) continue;
    const color = colorOf(p.kind);
    // The visible half of a merged run: the count badge STRETCHED — same
    // border, tint and mono count as a multi-op mark, spanning every turn of
    // the run, with kind-coloured segments keeping WHERE the writes and errors
    // sat along it.
    ctx.save();
    ctx.beginPath();
    ctx.rect(x0, y, x1 - x0, p.h);
    ctx.clip();
    for (const s of p.segs) {
      const mix = SPAN_SEG_MIX[s.kind] ?? 0;
      if (!mix || (neutralMarks && NEUTRAL_KINDS.has(s.kind))) continue;
      ctx.globalAlpha = p.selTurn != null && p.selTurn < s.first ? 0.35 : 1;
      ctx.fillStyle = alphaMix(colorOf(s.kind), mix);
      ctx.fillRect(X(railW + s.x0), y, Math.min(s.x1, p.width) - s.x0, p.h);
    }
    ctx.globalAlpha = 1;
    ctx.restore();
    ctx.strokeStyle = overMix(color, 60, theme.line);
    ctx.lineWidth = 1;
    ctx.strokeRect(x0 + 0.5, y + 0.5, x1 - x0 - 1, p.h - 1);
    const label = String(p.count);
    ctx.font = `500 8px ${SANS}`;
    if (textWidth(ctx, label) + 6 < x1 - x0) {
      ctx.fillStyle = color;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(label, (x0 + x1) / 2, y + p.h / 2 + 0.5);
    }
  }

  // --- marks ---
  for (let i = firstAtOrAfter(L.marks, yFrom); i < L.marks.length; i++) {
    const p = L.marks[i];
    if (p.y > yTo) break;
    if (p.t === "cellsel") {
      const y = Y(p.y);
      if (y + p.h < 0 || y > viewH) continue;
      ctx.fillStyle = theme.lineSoft;
      ctx.fillRect(X(p.x), y, p.w, p.h);
      continue;
    }
    if (p.t === "cellderiv") {
      // The cell a derivation step points at. Outlined, so it reads over the
      // selection fill rather than replacing it.
      const y = Y(p.y);
      if (y + p.h < 0 || y > viewH) continue;
      ctx.strokeStyle = theme.ink;
      ctx.lineWidth = 1;
      ctx.strokeRect(X(p.x) + 0.5, y + 0.5, p.w - 1, p.h - 1);
      continue;
    }
    const y = Y(p.y);
    const x = X(p.x);
    if (y < -20 || y > viewH + 20 || x < -20 || x > viewW + 20) continue;
    const color = colorOf(p.kind);
    ctx.globalAlpha = p.dim ? 0.35 : 1;
    if (p.count > 1) {
      ctx.font = `500 8px ${SANS}`;
      const label = String(p.count);
      const w = Math.max(13, textWidth(ctx, label) + 5);
      const hollow = p.kind === "read" || p.kind === "verified" || p.kind === "readCall";
      if (!hollow) {
        ctx.fillStyle = alphaMix(color, 18);
        ctx.fillRect(x - w / 2, y - 6.5, w, 13);
      }
      ctx.strokeStyle = overMix(color, 60, theme.line);
      ctx.lineWidth = 1;
      if (p.kind === "verified") ctx.setLineDash([2, 2]);
      ctx.strokeRect(x - w / 2 + 0.5, y - 6, w - 1, 12);
      ctx.setLineDash([]);
      ctx.fillStyle = color;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(label, x, y + 0.5);
    } else if (p.kind === "prompt" || p.kind === "reply" || p.kind === "note") {
      // Conversation marks are the ruler beats' own diamonds — shape is what
      // keeps "a beat of the conversation" and "work on a subject" apart at a
      // glance. A note is the same diamond a size down: it belongs to the
      // conversation, but it is said in passing rather than being a boundary.
      diamond(ctx, x, y, p.kind === "note" ? 3 : 4.5);
      if (p.kind === "reply") { ctx.strokeStyle = color; ctx.lineWidth = 1; ctx.stroke(); }
      else { ctx.fillStyle = color; ctx.fill(); }
    } else {
      // Square on purpose — hollow = consulted, filled = changed, red = a call
      // here errored.
      const hollow = p.kind === "read" || p.kind === "verified" || p.kind === "readCall";
      if (p.kind === "verified") ctx.setLineDash([2, 2]);
      if (hollow) {
        ctx.strokeStyle = color;
        ctx.lineWidth = 1;
        ctx.strokeRect(x - 4 + 0.5, y - 4 + 0.5, 7, 7);
      } else {
        ctx.fillStyle = color;
        ctx.fillRect(x - 4, y - 4, 8, 8);
      }
      ctx.setLineDash([]);
    }
    ctx.globalAlpha = 1;
  }

  // --- influence flow lines (z1: under the pins and the rail) ---
  for (const p of L.links) {
    if (p.t === "link") {
      const x1 = X(p.x1);
      const y1 = Y(p.y1);
      const x2 = X(p.x2);
      const y2 = Y(p.y2);
      if (Math.max(y1, y2) < 0 || Math.min(y1, y2) > viewH) continue;
      if (Math.max(x1, x2) < 0 || Math.min(x1, x2) > viewW) continue;
      const color = p.hue || theme[TIER_TOKEN[p.tier]] || theme.mutedSoft;
      ctx.globalAlpha = tierOpacity(p.tier);
      ctx.strokeStyle = color;
      ctx.lineWidth = p.w || tierStroke(p.tier);
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      if (p.straight) ctx.lineTo(x2, y2);
      else ctx.bezierCurveTo(x1 + p.h, y1, x2 - p.h, y2, x2, y2);
      ctx.stroke();
      // Source end open, target end an arrow: the evidence points AT what was
      // built from it.
      ctx.beginPath();
      ctx.arc(x1, y1, 2.5, 0, Math.PI * 2);
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.save();
      ctx.translate(x2, y2);
      ctx.rotate((p.angle * Math.PI) / 180);
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(-5, -3);
      ctx.lineTo(-5, 3);
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.fill();
      ctx.restore();
      ctx.globalAlpha = 1;
      continue;
    }
    if (p.t === "linkanchor" || p.t === "callring") {
      const y = Y(p.y);
      if (y < -20 || y > viewH + 20) continue;
      // A call ring is the anchor's own glyph, lighter: the same call as the
      // mark the reader is on, not the mark itself.
      ctx.globalAlpha = p.t === "callring" ? 0.35 : 0.55;
      ctx.beginPath();
      ctx.arc(X(p.x), y, p.r, 0, Math.PI * 2);
      ctx.strokeStyle = theme.ink;
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.globalAlpha = 1;
      continue;
    }
    if (p.t === "linkdrop") {
      // A cap that says nothing reads as "this is all of it".
      ctx.font = `9px ${SANS}`;
      ctx.fillStyle = theme.muted;
      ctx.textAlign = "left";
      ctx.textBaseline = "alphabetic";
      ctx.fillText(`+${p.n} weaker`, X(p.x), Y(p.y));
    }
  }

  // --- cell pins and follow rings ---
  // Drawn BEFORE the rail, so a pin whose turn scrolls out of the window
  // disappears under the rail exactly like the cell it annotates (at a higher
  // layer it floated over the lane labels — found live).
  for (const p of L.pins) {
    const y = Y(p.y);
    const x = X(p.x);
    if (p.t === "ring") {
      if (y < -20 || y > viewH + 20 || x < -20 || x > viewW + 20) continue;
      // A circular reticle in the join surface's own dot vocabulary: a
      // rounded-rect read as a foreign glyph (found live).
      ctx.beginPath();
      ctx.arc(x, y, p.r, 0, Math.PI * 2);
      // The graph's rings mark the cells an open claim rests on, in the
      // graph's own colour rather than the join blue.
      const ringColor = p.finding ? (p.tone === "danger" ? theme.red : p.tone === "warn" ? theme.amber : theme.ink) : p.graph ? theme.graph : theme.join;
      ctx.fillStyle = alphaMix(ringColor, 12);
      ctx.fill();
      ctx.strokeStyle = ringColor;
      ctx.lineWidth = 1.5;
      ctx.stroke();
      continue;
    }
    if (p.t === "fpin") {
      if (y < -20 || y > viewH + 20 || x < -20 || x > viewW + 20) continue;
      // A finding is a judgment about the cell, so it borrows the ledger's
      // severity colours rather than the join blue or the mark's kind colour:
      // red for danger, amber for a warning, the muted ink for the rest. A
      // filled diamond -- the review strip's dot would read as one more mark.
      const color = p.tone === "danger" ? theme.red : p.tone === "warn" ? theme.amber : theme.muted;
      const s = p.s;
      ctx.beginPath();
      ctx.moveTo(x, y - s);
      ctx.lineTo(x + s, y);
      ctx.lineTo(x, y + s);
      ctx.lineTo(x - s, y);
      ctx.closePath();
      ctx.fillStyle = overMix(color, 82, theme.bg);
      ctx.fill();
      ctx.strokeStyle = alphaMix(theme.ink, 62);
      ctx.lineWidth = 1;
      ctx.stroke();
      continue;
    }
    if (p.t === "gpin") {
      if (y < -20 || y > viewH + 20 || x < -20 || x > viewW + 20) continue;
      // The knowledge graph's own glyph, its node dot, in the graph's hue and
      // filled by how firmly the chat's claims stand on the cell: solid under a
      // supported claim, a ring under an unsupported one, a fainter ring where
      // the walk placed an anchor no claim cites.
      ctx.beginPath();
      ctx.arc(x, y, p.r, 0, Math.PI * 2);
      if (p.tone === "supported") {
        ctx.fillStyle = theme.graph;
        ctx.fill();
        ctx.strokeStyle = theme.bg;
        ctx.lineWidth = 1;
      } else {
        ctx.fillStyle = theme.bg;
        ctx.fill();
        ctx.strokeStyle = p.tone === "insufficient" ? theme.graph : alphaMix(theme.graph, 55);
        ctx.lineWidth = 1.25;
      }
      ctx.stroke();
      continue;
    }
    if (y < -20 || y > viewH + 20 || x < -40 || x > viewW + 40) continue;
    drawJoinChip(ctx, theme, x, y, p);
  }

  // --- the rail, opaque, over the plane it must occlude ---
  // A scene built with no rail (a card on the trace canvas, whose rows are
  // labelled inside themselves so its first mark sits at its first moment)
  // draws none: not even the gutter glyphs, which would land on its marks.
  const railX = pinX ? ox : X(0);
  if (railW > 0) {
  ctx.fillStyle = theme.bg;
  ctx.fillRect(railX, oy, railW, railH ?? viewH);
  const drawRailRow = (p, y) => {
    // The row's strip continues under the rail at the same strength as on the
    // plane (the opaque twin: the rail must occlude the scrolling plane), so
    // the label, its sparkline and its marks sit on one ground and the 1px
    // seam separates rows. One primitive; the stronger card tried here read as
    // a second surface (found live).
    ctx.fillStyle = overMix(theme.controlBg, 45, theme.bg);
    ctx.fillRect(railX, y + 1, railW, p.h - 2);
    if (p.selected || p.hovered) {
      ctx.fillStyle = rowWash(theme, p);
      ctx.fillRect(railX, y, railW, p.h);
    }
    if (p.selected) {
      // rowChromeClass's inset 2px green bar: the active row's mark in the tree.
      ctx.fillStyle = theme.green;
      ctx.fillRect(railX, y, 2, p.h);
    }
    // Nesting is drawn the way StickyFolder draws it: a hairline guide down
    // every open ancestor's gutter, under that ancestor's chevron. Rows are
    // contiguous, so the per-row segments read as one line.
    if (p.depth > 0) {
      ctx.fillStyle = theme.lineSoft;
      for (let k = 0; k < p.depth; k++) {
        ctx.fillRect(railX + RAIL_GUTTER_X + k * RAIL_INDENT + RAIL_GUTTER_W / 2, y, 1, p.h);
      }
    }
    if (p.session) {
      // A session head: its hue, its name in the entry face, and where and
      // when it ran under it. Chevron in the gutter like any head.
      const s = p.session;
      if (!s.first) {
        ctx.fillStyle = theme.lineStrong;
        ctx.fillRect(railX, y, railW, 1);
      }
      ctx.fillStyle = overMix(theme.controlBg, 70, theme.bg);
      ctx.fillRect(railX, y + 1, railW, p.h - 2);
      const gxs = railX + RAIL_GUTTER_X + RAIL_GUTTER_W / 2;
      drawChevron(ctx, gxs, y + 15, !p.closed, theme.muted);
      let tx = gxs + RAIL_GUTTER_W / 2 + RAIL_LABEL_GAP;
      if (s.hue) {
        ctx.fillStyle = s.hue;
        ctx.beginPath();
        ctx.arc(tx + 4, y + 15, 4, 0, Math.PI * 2);
        ctx.fill();
        tx += 14;
      }
      const room = railX + railW - RAIL_PAD_X - tx;
      ctx.textAlign = "left";
      ctx.textBaseline = "alphabetic";
      ctx.font = `500 12px ${SANS}`;
      ctx.fillStyle = theme.ink;
      ctx.fillText(clipText(ctx, String(p.label || ""), room), tx, y + 19);
      if (s.sub) {
        ctx.font = `10.5px ${SANS}`;
        ctx.fillStyle = theme.muted;
        ctx.fillText(clipText(ctx, String(s.sub), room), tx, y + 34);
      }
      return;
    }
    // A top-level group header is the sidebar's section eyebrow (VIEWS, BREAK
    // DOWN BY); every other row is an entry row, folder or leaf alike.
    const group = p.foldable && p.depth === 0;
    // A group row is taller than a lane row by a few pixels of air; the unit
    // (label over sparkline) keeps its own metrics and centres in the extra.
    // A compact row has no sparkline, so its one line centres in the row.
    const air = p.compact ? (p.h - RAIL_LABEL_BASELINE - 4) / 2 : Math.max(0, (p.h - ROW_H) / 2);
    // Every row carries the gutter cell: a chevron for a row that opens, the
    // tree's leaf dash for one that does not.
    const gx = railX + RAIL_GUTTER_X + p.depth * RAIL_INDENT + RAIL_GUTTER_W / 2;
    if (p.expandable || p.foldable) {
      drawChevron(ctx, gx, y + air + RAIL_GLYPH_CY, p.foldable ? !p.closed : p.expanded, group ? theme.eyebrow : theme.muted);
    } else {
      drawLeafMark(ctx, gx, y + air + RAIL_GLYPH_CY, theme.mutedSoft);
    }
    const tx = gx + RAIL_GUTTER_W / 2 + RAIL_LABEL_GAP;
    // The row's activity over the WHOLE axis, beneath its title and from the
    // title's left edge, as the tree draws it: the gutter and its guides stay
    // clear, and rows at one depth share an axis. Lane weight rides with it as
    // the tree's share tint behind the curve, the row's share of the
    // conversation's tokens. WEIGHT_FILL in the host is the legend swatch and
    // must agree.
    const pinW = p.pinLabel ? SYS_PIN_W : 0;
    const sparkW = railX + railW - RAIL_PAD_X - pinW - tx;
    if (p.spark && !p.compact && sparkW > 24) drawSpark(ctx, theme, p.spark, tx, y + p.h - air - RAIL_SPARK_FLOOR - RAIL_SPARK_H, sparkW, RAIL_SPARK_H, p.weightRatio);
    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";
    // The system header's pin control is right-aligned in the cell, so the
    // label clips before it.
    const eyebrow = group && upperGroups;
    ctx.font = eyebrow ? EYEBROW_FONT : RAIL_TITLE_FONT;
    ctx.letterSpacing = eyebrow ? EYEBROW_TRACKING : "0px";
    const label = clipText(ctx, eyebrow ? String(p.label).toUpperCase() : p.label, railX + railW - RAIL_PAD_X - pinW - tx);
    ctx.fillStyle = eyebrow ? (p.selected || p.hovered ? theme.ink : theme.eyebrow) : group ? theme.ink : alphaMix(theme.ink, 80);
    ctx.fillText(label, tx, y + air + RAIL_LABEL_BASELINE);
    ctx.letterSpacing = "0px";
    if (p.pinLabel) {
      ctx.font = `9px ${SANS}`;
      ctx.fillStyle = p.pinned ? theme.ink : theme.mutedSoft;
      ctx.textAlign = "right";
      ctx.fillText(p.pinLabel, railX + railW - 6, y + air + RAIL_LABEL_BASELINE);
    }
  };
  for (let i = firstAtOrAfter(L.rail, yFrom); i < L.rail.length; i++) {
    const p = L.rail[i];
    if (p.y > yTo) break;
    if (sysPinned && p.system) continue;
    drawRailRow(p, Y(p.y));
  }
  ctx.strokeStyle = theme.lineSoft;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(railX + railW - 0.5, oy);
  ctx.lineTo(railX + railW - 0.5, oy + (railH ?? viewH));
  ctx.stroke();
  }

  // --- spliced insets (z3: above the rail, so the bracket is never swallowed) ---
  for (const p of L.insets) {
    if (p.t === "insetnote") {
      // What the box says while it has no timeline: the reason, and the house
      // sweep under it when the reason is "still fetching".
      //
      // Positioned exactly like the box that holds it -- scrolled vertically,
      // sticky against the horizontal pan. Using the pinned strip transform
      // left the note hanging at a fixed viewport position while its own box
      // scrolled away from underneath it.
      const x = ox + p.x;
      const y = Y(p.y);
      if (y + p.h < -40 || y > viewH + 40) continue;
      // The note is the head's second line: same left edge as the name above
      // it, same slot a ready child's stats take.
      ctx.font = `10px ${SANS}`;
      ctx.fillStyle = p.busy ? theme.mutedSoft : theme.muted;
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText(clipText(ctx, p.text, p.w), x, y + p.h / 2);
      if (p.busy && sweep != null) {
        const lineY = y + p.h + 1;
        ctx.fillStyle = alphaMix(theme.ink, 14);
        ctx.fillRect(x, lineY, p.w, 2);
        const band = Math.max(50, p.w * 0.25);
        const at = x - band + (p.w + band * 2) * sweep;
        const g = ctx.createLinearGradient(at, 0, at + band, 0);
        g.addColorStop(0, alphaMix(theme.mutedSoft, 0));
        g.addColorStop(0.5, alphaMix(theme.mutedSoft, 85));
        g.addColorStop(1, alphaMix(theme.mutedSoft, 0));
        ctx.save();
        ctx.beginPath();
        ctx.rect(x, lineY - 1, p.w, 4);
        ctx.clip();
        ctx.fillStyle = g;
        ctx.fillRect(at, lineY - 1, band, 4);
        ctx.restore();
      }
      continue;
    }
    if (p.t === "connector") {
      const bx = p.side === "l" ? ox + p.boxX + 10 : ox + p.boxX + p.boxW - 10;
      ctx.strokeStyle = alphaMix(theme.muted, 55);
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(X(p.x1), Y(p.y1));
      ctx.lineTo(bx, Y(p.y2));
      ctx.stroke();
      ctx.setLineDash([]);
      continue;
    }
    if (p.t === "insetbox") {
      const y = Y(p.y);
      if (y + p.h < -40 || y > viewH + 40) continue;
      // Sticky against the horizontal pan: the child has its own clock, so its
      // box deliberately does not travel with the parent's turn axis.
      const x = ox + p.x;
      // The same ground as the plane around it: the child is a continuation
      // of the reading, not a card laid on top of it, so only the outline
      // says where the sub-scene begins.
      ctx.fillStyle = theme.bg;
      roundRect(ctx, x, y, p.w, p.h, 2);
      ctx.fill();
      ctx.strokeStyle = theme.lineStrong;
      ctx.lineWidth = 1;
      roundRect(ctx, x + 0.5, y + 0.5, p.w - 1, p.h - 1, 2);
      ctx.stroke();
      // The head, two lines: the thread's name and what the reading is, then
      // the child's own stats.
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.font = `11px ${SANS}`;
      ctx.fillStyle = theme.ink;
      // The head's two line centres, from the scene: a note box sets its name
      // and reason a little lower than a ready child's head sets name and stats.
      const [line1, line2] = p.lines || INSET_HEAD_LINES;
      // The NAME gets the room first — it is the thread's identity, and the
      // descriptor is a gloss. Allocating the gloss first truncated
      // "Implement 8 PR review fixes" to "Impleme…" in a narrow split while
      // the sentence beside it stayed whole.
      const openW = p.openable ? 100 : 10;
      const avail = Math.max(40, p.w - 20 - openW);
      const name = clipText(ctx, p.label, avail);
      const nameW = textWidth(ctx, name);
      ctx.fillText(name, x + 10, y + line1);
      const glossW = avail - nameW - 10;
      // Below this there is no room for a gloss that reads as a sentence, and a
      // two-word stub beside the name says less than nothing.
      if (glossW >= 90) {
        ctx.font = `10px ${SANS}`;
        ctx.fillStyle = theme.mutedSoft;
        ctx.fillText(
          clipText(ctx, "the spawned thread's own timeline · read as its own conversation", glossW),
          x + 20 + nameW, y + line1,
        );
      }
      if (p.openable) {
        ctx.font = `10px ${SANS}`;
        ctx.textAlign = "right";
        ctx.fillStyle = theme.muted;
        ctx.fillText("open full trace", x + p.w - 10, y + line1);
        ctx.textAlign = "left";
      }
      // The child's own zoom, drawn from the same regions the scene hit-tests:
      // a thread has its own axis, so it has its own zoom.
      for (const z of p.zoomBtns || []) {
        const glyph = z.kind === "out" ? "\u2212" : z.kind === "in" ? "+" : "\u2922";
        const on = z.kind === "fit" ? p.zoom?.fitted : true;
        ctx.font = `11px ${SANS}`;
        ctx.textAlign = "center";
        ctx.fillStyle = on && z.kind === "fit" ? theme.ink : theme.muted;
        ctx.fillText(glyph, x - INSET_PAD_X + (z.x0 + z.x1) / 2, y + line1);
        ctx.textAlign = "left";
      }
      if (p.stats) {
        ctx.font = `10px ${SANS}`;
        ctx.fillStyle = theme.muted;
        ctx.fillText(clipText(ctx, p.stats, p.w - 20), x + 10, y + line2);
      }
      continue;
    }
    // The child plane, drawn into the same canvas, clipped to its box. This is
    // the shape a multi-session workbench takes: one canvas, N scenes, each
    // with its own axis and its own pan.
    const y = Y(p.y);
    if (y + p.clipH < -40 || y > viewH + 40) continue;
    ctx.save();
    ctx.beginPath();
    ctx.rect(ox + p.x, y, p.clipW, p.clipH);
    ctx.clip();
    // viewW/viewH stay the CANVAS viewport's: culling inside the child is a
    // question about the screen, and the box's own bounds are the clip's job.
    // Passing the box height here compared absolute y against 120px and culled
    // every row the box existed to show.
    // The child's own pan: a zoomed child is wider than its box, and zoom
    // without pan is half a control. Its rail stays put (pinX) while its axis
    // moves under it, exactly as the parent plane's does.
    drawScene(ctx, p.scene, theme, {
      scrollX: p.scene.meta?.zoom?.panX || 0, scrollY: 0, viewW: p.clipW, viewH,
      ox: ox + p.x, oy: y, pinX: true, pinY: false, railH: p.clipH,
    });
    ctx.restore();
  }

  // --- the keyboard focus ring ---
  // Drawn over the plane so it is never lost under a pin or a span; a focus
  // indicator that can be occluded is not one. Its position comes from the
  // plane's own focus state through the scene's geometry, not from a prim — the
  // scene describes the model, and where the keyboard happens to be is not part
  // of the model.
  //
  // Only while the plane HOLDS focus. A pointer pick also moves the ring, so a
  // ring that outlived the focus left a box sitting on the last thing clicked
  // long after the reader had moved on — read as a stuck selection.
  const fr = focus && focused ? focusRect(scene, focus) : null;
  if (fr) {
    const y = Y(fr.y0);
    if (y + (fr.y1 - fr.y0) >= 0 && y <= viewH) {
      // Two strokes: a light halo under an ink ring, so the indicator holds
      // against a filled mark as well as against the plane.
      ctx.setLineDash([]);
      ctx.strokeStyle = alphaMix(theme.bg, 90);
      ctx.lineWidth = 3;
      ctx.strokeRect(X(fr.x0) - 2, y - 2, fr.x1 - fr.x0 + 4, fr.y1 - fr.y0 + 4);
      ctx.strokeStyle = theme.ink;
      ctx.lineWidth = 1.5;
      ctx.strokeRect(X(fr.x0) - 2, y - 2, fr.x1 - fr.x0 + 4, fr.y1 - fr.y0 + 4);
    }
  }

  // --- edge pointer chips ---
  // Lanes whose activity is outside the visible turn window get a chip at the
  // edge it left, saying where the nearest mark is and how to get there. The
  // window is a per-frame fact, so the chips are computed here rather than baked
  // into the scene: `edgeChipsAt` binary-searches each lane's sorted turns,
  // which is cheaper than rebuilding a scene on every pan frame.
  const chipHits = [];
  if (pinX && pinY && scene.turnCount > 0) {
    const fromTurn = Math.max(0, Math.floor((scrollX - railW) / scene.cellW));
    const toTurn = Math.min(scene.turnCount - 1, Math.ceil((scrollX - railW + viewW) / scene.cellW));
    ctx.font = `9px ${SANS}`;
    ctx.textBaseline = "middle";
    for (const c of edgeChipsAt(scene, fromTurn, toTurn)) {
      const y = Y(c.y);
      if (y + CHIP_H < 0 || y > viewH) continue;
      const label = c.dir === "left" ? `\u2039 T${c.turn}` : `T${c.turn} \u203a`;
      const w = textWidth(ctx, label) + 8;
      const x = c.dir === "left" ? ox + railW + 3 : ox + viewW - 3 - w;
      ctx.fillStyle = overMix(theme.controlBg, 100, theme.bg);
      roundRect(ctx, x, y, w, CHIP_H, 2);
      ctx.fill();
      ctx.strokeStyle = theme.line;
      ctx.lineWidth = 1;
      roundRect(ctx, x + 0.5, y + 0.5, w - 1, CHIP_H - 1, 2);
      ctx.stroke();
      ctx.fillStyle = theme.muted;
      ctx.textAlign = "left";
      ctx.fillText(label, x + 4, y + CHIP_H / 2);
      chipHits.push({ x0: x, x1: x + w, y0: y, y1: y + CHIP_H, laneId: c.laneId, turn: c.turn, label: c.label });
    }
  }

  // --- related-row pointers ---
  // Rows the current reading relates to, chipped at the edge they left. Like
  // the edge chips these are a function of the VIEWPORT, so they are computed
  // in the draw: the scene marks which rows are related, and where the band is
  // changes on every scroll frame.
  if (pinX && pinY) {
    const relatedIds = new Set(L.rows.filter((p) => p.t === "row" && p.related).map((p) => p.laneId));
    if (relatedIds.size) {
      const paths = new Set(scene.rows
        .filter((r) => r.type === "lane" && relatedIds.has(r.lane.id))
        .map((r) => r.lane.path)
        .filter(Boolean));
      const view = { top: scrollY, height: viewH, left: scrollX, width: viewW };
      ctx.font = `9px ${SANS}`;
      ctx.textBaseline = "middle";
      for (const c of relatedChips(scene.rows, scene.tops, paths, view, scene.headerH)) {
        const y = Y(c.top);
        if (y + 16 < 0 || y > viewH) continue;
        const label = c.dir === "up" ? `\u2191 ${c.lane.label}`
          : c.dir === "down" ? `\u2193 ${c.lane.label}` : c.lane.label;
        const text = clipText(ctx, label, 212);
        const w = Math.min(220, textWidth(ctx, text) + 8);
        const x = ox + railW + 3;
        ctx.fillStyle = overMix(theme.controlBg, 100, theme.bg);
        roundRect(ctx, x, y, w, 16, 2);
        ctx.fill();
        ctx.strokeStyle = alphaMix(theme.lineStrong, 70);
        ctx.lineWidth = 1;
        roundRect(ctx, x + 0.5, y + 0.5, w - 1, 15, 2);
        ctx.stroke();
        ctx.fillStyle = theme.ink;
        ctx.textAlign = "left";
        ctx.fillText(text, x + 4, y + 8);
        chipHits.push({
          x0: x, x1: x + w, y0: y, y1: y + 16,
          related: true, rowTop: c.rowTop, laneId: c.lane.id, label: c.lane.label, dir: c.dir,
        });
      }
    }
  }

  // --- strip-mark guide ---
  // A hovered strip mark whose cells are all off the band points at the
  // nearest: a chip on the edge it left, in that cell's own column, naming the
  // row. A function of the scroll like the chips above, so it is placed here.
  let drawnGuide = null;
  if (pinX && pinY && guide?.length) {
    const g = guideChip(guide, { top: scrollY, height: viewH }, scene.headerH, pinnedBandInset(sysMeta, { scrollY, oy, viewH }));
    if (g) {
      ctx.font = `9px ${SANS}`;
      ctx.textBaseline = "middle";
      const arrow = g.dir === "up" ? "\u2191" : "\u2193";
      const text = clipText(ctx, `${arrow} ${g.target.label}${g.more > 0 ? ` +${g.more}` : ""}`, 212);
      const w = Math.min(220, textWidth(ctx, text) + 8);
      const x = Math.max(ox + railW + 3, Math.min(ox + viewW - 3 - w, X(g.target.x) - w / 2));
      const y = Y(g.top);
      ctx.fillStyle = overMix(theme.controlBg, 100, theme.bg);
      roundRect(ctx, x, y, w, 16, 2);
      ctx.fill();
      ctx.strokeStyle = alphaMix(theme.lineStrong, 70);
      ctx.lineWidth = 1;
      roundRect(ctx, x + 0.5, y + 0.5, w - 1, 15, 2);
      ctx.stroke();
      ctx.fillStyle = theme.ink;
      ctx.textAlign = "left";
      ctx.fillText(text, x + 4, y + 8);
      drawnGuide = { dir: g.dir, label: text, laneId: g.target.laneId, turn: g.target.turn, more: g.more, x0: x, x1: x + w, y0: y, y1: y + 16 };
    }
  }

  // --- the sticky strip stack (z4: over everything) ---
  // The strips span the WHOLE axis while a few hundred pixels of it are on
  // screen, and every one of their marks was issuing a real fillRect or
  // fillText regardless. On a 700,000px axis that is ~39,000 density bars drawn
  // off-screen per frame, which was the draw's actual cost -- not the row-bound
  // layers, which already culled.
  const xFrom = scrollX - ox - CULL_SLACK;
  const xTo = scrollX - ox + viewW + CULL_SLACK;
  for (const p of L.strips) {
    // `striprow` is the band itself and `dline` is one polyline over the whole
    // axis; both are single draws and neither has a single x to test.
    if (p.x != null && (p.x + (p.w || p.extent || 0) < xFrom || p.x > xTo)) continue;
    if (p.t === "striprow") {
      const y = SY(p.y);
      ctx.fillStyle = theme.bg;
      ctx.fillRect(railX, y, viewW, p.h);
      ctx.strokeStyle = theme.lineSoft;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(railX, y + p.h - 0.5);
      ctx.lineTo(railX + viewW, y + p.h - 0.5);
      ctx.stroke();
      continue;
    }
    if (p.t === "axistick") {
      ctx.fillStyle = theme.lineStrong;
      ctx.fillRect(SX(p.x), SY(p.y), 1, p.h);
      continue;
    }
    if (p.t === "axisbreak") {
      // The investigation timeline's break glyph, the gap's length beside it
      // when the break is wide enough to carry the words.
      const cx = SX(p.x);
      const y = SY(p.y);
      ctx.strokeStyle = theme.mutedSoft;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(cx - 4, y + 16); ctx.lineTo(cx - 2, y + 9);
      ctx.moveTo(cx, y + 16); ctx.lineTo(cx + 2, y + 9);
      ctx.stroke();
      if (p.label && p.w > 34) {
        ctx.font = `10px ${SANS}`;
        ctx.fillStyle = theme.mutedSoft;
        ctx.textAlign = "center";
        ctx.fillText(p.label, cx + 1, y + 8);
        ctx.textAlign = "left";
      }
      continue;
    }
    if (p.t === "occ") {
      // The topic's hue where a dated request placed the stretch in one;
      // the house grey where none did.
      ctx.fillStyle = p.hue || theme.lineStrong;
      ctx.fillRect(SX(p.x), SY(p.y), p.w, p.h);
      continue;
    }
    if (p.t === "stripwait") {
      // The house shimmer, drawn rather than animated by CSS: a canvas has no
      // cascade, so the sweep's position comes from the clock the draw is
      // already given. Reduced motion gets the resting hairline and no sweep.
      const x = SX(p.x);
      ctx.fillStyle = alphaMix(theme.ink, 14);
      ctx.fillRect(x, SY(p.y), p.w, p.h);
      if (sweep != null) {
        const band = Math.max(60, p.w * 0.25);
        const at = x - band + (p.w + band * 2) * sweep;
        const g = ctx.createLinearGradient(at, 0, at + band, 0);
        g.addColorStop(0, alphaMix(theme.mutedSoft, 0));
        g.addColorStop(0.5, alphaMix(theme.mutedSoft, 85));
        g.addColorStop(1, alphaMix(theme.mutedSoft, 0));
        ctx.save();
        ctx.beginPath();
        ctx.rect(x, SY(p.y) - 1, p.w, p.h + 2);
        ctx.clip();
        ctx.fillStyle = g;
        ctx.fillRect(at, SY(p.y) - 1, band, p.h + 2);
        ctx.restore();
      }
      continue;
    }
    if (p.t === "beat") {
      // The prompt opening the episode the pointer is inside reads hot, which
      // is the ruler's echo of the band drawn below it: a draw-time fact from
      // the pointer, like the band, and the only source of it.
      const hot = p.kind === "prompt" && episode?.prev === p.turn;
      const color = p.kind === "prompt" ? (hot ? theme.ink : theme.eyebrow) : theme.muted;
      diamond(ctx, SX(p.x), SY(p.y), p.kind === "prompt" ? 4.5 : 4);
      if (p.kind === "prompt") { ctx.fillStyle = color; ctx.fill(); }
      else { ctx.strokeStyle = color; ctx.lineWidth = 1; ctx.stroke(); }
      continue;
    }
    if (p.t === "dbar") {
      ctx.fillStyle = p.hue || theme.lineStrong;
      ctx.fillRect(SX(p.x), SY(p.y), p.w, p.h);
      continue;
    }
    if (p.t === "dline") {
      // The running total, left to right: where the line climbs steeply is
      // where the trace got expensive fast.
      ctx.strokeStyle = alphaMix(theme.muted, 55);
      ctx.lineWidth = 1;
      ctx.beginPath();
      p.pts.forEach(([x, y], i) => (i ? ctx.lineTo(SX(x), SY(y)) : ctx.moveTo(SX(x), SY(y))));
      ctx.stroke();
      continue;
    }
    if (p.t === "scanwin") {
      ctx.fillStyle = theme.lineStrong;
      ctx.fillRect(SX(p.x), SY(p.y), p.w, p.h);
      continue;
    }
    if (p.t === "scanbar") {
      ctx.fillStyle = alphaMix(theme[p.token] || theme.info, 45);
      ctx.fillRect(SX(p.x), SY(p.y), p.w, p.h);
      continue;
    }
    if (p.t === "scanseg") {
      // A chapter is a report's window, and the strip says so in the language
      // the other scan strips already speak for what they covered and where
      // their traffic sat: a 2px bar along the band's foot, the marks on the
      // centre line above it. Numbered boxes and a ticked axis both read as
      // invented widgets (found live, twice); the ordinal is one hover away and
      // in the dock. Abutting chapters keep a hairline between them, and a
      // chapter that raised something takes the finding's own colour.
      const calm = p.tone !== "warn" && p.tone !== "danger";
      ctx.fillStyle = calm ? theme.lineStrong : alphaMix(p.tone === "danger" ? theme.red : theme.amber, 70);
      ctx.fillRect(SX(p.x), SY(p.y) + p.h - 1, Math.max(1, p.w - 1), 2);
      continue;
    }
    if (p.t === "scanspan") {
      // An incident's reach: a 2px bar in the finding's own colour on the line
      // the traffic bars use, under the occurrence marks.
      const color = p.tone === "danger" ? theme.red : p.tone === "warn" ? theme.amber : theme.muted;
      ctx.fillStyle = alphaMix(color, 80);
      ctx.fillRect(SX(p.x), SY(p.y), Math.max(1, p.w), p.h);
      continue;
    }
    if (p.t === "scanarc") {
      // The selected finding's fan. A quadratic arc bulging into the plane,
      // dashed for a related-but-not-merged occurrence, a dot at the far end
      // so the arc reads as pointing at something.
      const x1 = SX(p.x1), x2 = SX(p.x2), y = SY(p.y);
      if (Math.max(x1, x2) < railX || Math.min(x1, x2) > railX + viewW) continue;
      const color = p.tone === "danger" ? theme.red : p.tone === "warn" ? theme.amber : theme.muted;
      ctx.save();
      ctx.globalAlpha = (p.hover ? 0.55 : 1) * (p.soft ? 0.55 : 0.8);
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.25;
      ctx.setLineDash(p.soft ? [3, 3] : []);
      ctx.beginPath();
      ctx.moveTo(x1, y);
      ctx.quadraticCurveTo((x1 + x2) / 2, y + p.bulge * 2, x2, y);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.arc(x2, y, 2, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.restore();
      continue;
    }
    if (p.t === "scanmark") {
      // One family with the joins strip: a lone finding is a dot, several fold
      // into a counted pill, and a fold that spans turns trails a bar to where
      // it reaches. Same silhouettes, same weights, same reach — the scan
      // strips sit directly above the joins strip, and two vocabularies for
      // "N of these, over this stretch" made the pair unreadable.
      //
      // Confidence renders as fill weight, never as a second colour: a scan
      // strip says one kind of thing, and how sure it is. The weights have a
      // FLOOR — the old 22% fill on a "possible" mark read as a rendering
      // artefact rather than a quieter finding — and every mark keeps the same
      // defined edge, so low confidence is quieter, never absent.
      const color = theme[p.token] || theme.info;
      const tint = p.weight === "clear" || p.weight === "alarm" ? 85
        : p.weight === "likely" || p.weight === "warn" ? 55 : 30;
      const x = SX(p.x);
      // Every part of the mark hangs off the scene's own centre line. No local
      // offsets: that is what let the glyph drift off its hit box.
      const cy = SY(p.cy);
      const half = (() => {
        if (p.count <= 1) return 4;
        ctx.font = `500 8px ${SANS}`;
        return Math.max(14, textWidth(ctx, String(p.count)) + 6) / 2;
      })();
      // The reach, drawn first so the glyph sits over it.
      const barFrom = x + half + 2;
      const barTo = SX(p.endX);
      if (p.through > p.turn && barTo > barFrom + 2) {
        ctx.fillStyle = alphaMix(color, 38);
        ctx.fillRect(barFrom, cy - 1, barTo - barFrom, 2);
      }
      if (p.count > 1) {
        const w = half * 2;
        ctx.fillStyle = overMix(color, tint * 0.32, theme.bg);
        roundRect(ctx, x - w / 2, cy - 6, w, 12, 6);
        ctx.fill();
        ctx.strokeStyle = alphaMix(theme.ink, 62);
        ctx.lineWidth = 1;
        roundRect(ctx, x - w / 2 + 0.5, cy - 5.5, w - 1, 11, 5.5);
        ctx.stroke();
        ctx.fillStyle = theme.ink;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(String(p.count), x, cy + 0.5);
      } else {
        ctx.beginPath();
        ctx.arc(x, cy, 4, 0, Math.PI * 2);
        ctx.fillStyle = overMix(color, tint, theme.bg);
        ctx.fill();
        ctx.strokeStyle = alphaMix(theme.ink, 62);
        ctx.lineWidth = 1;
        ctx.stroke();
      }
      continue;
    }
    if (p.t === "jspan") {
      const x = SX(p.x);
      const y = SY(p.y);
      const chipW = p.dot ? drawJoinDot(ctx, theme, x, y, p.hue) : drawJoinChip(ctx, theme, x, y, p);
      if (p.through > p.turn && p.extent > chipW + 4) {
        ctx.fillStyle = alphaMix(p.hue || theme.join, 38);
        ctx.fillRect(x + chipW + 2, y + 5, p.extent - chipW - 2, 2);
      }
    }
  }

  // --- the telemetry band, pinned to the bottom edge ---
  // The mirror of the strip stack: opaque, drawn last, over everything that now
  // scrolls beneath it. Its flow height is still reserved, so nothing above it
  // moves -- only where it is painted changes, and only while its own place in
  // the flow has left the viewport.
  // Unconditional once pinned: the row and rail loops skip the band's prims
  // whenever it is pinned, so anything this pass declines to draw is drawn
  // NOWHERE. Only the opaque ground and the rule are conditional -- they are
  // what a band floating over content needs and a band sitting in its own
  // place does not.
  if (sysPinned) {
    const top = Y(sysMeta.top) + sysShift;
    if (sysShift < 0) {
      ctx.fillStyle = theme.bg;
      ctx.fillRect(ox, top, viewW, sysMeta.h);
    }
    // Both layers are ordered by y and the band is the last thing in them, so
    // the search starts at its own top rather than walking every lane on the
    // plane to find a handful of prims -- on a 700-lane plane that walk is the
    // pass's whole cost.
    for (let i = firstAtOrAfter(L.rows, sysMeta.top); i < L.rows.length; i++) {
      const q = L.rows[i];
      if (q.system) drawRowBg(q, RY(q, Y(q.y)));
    }
    for (let i = firstAtOrAfter(L.rail, sysMeta.top); i < L.rail.length; i++) {
      const q = L.rail[i];
      if (q.system) drawRailRow(q, RY(q, Y(q.y)));
    }
    if (sysShift < 0) {
      ctx.fillStyle = theme.line;
      ctx.fillRect(ox, top, viewW, 1);
    }
  }

  // The hovered topic run, from the ribbon that named it down through the
  // lanes. Drawn AFTER the strip stack: the strips are opaque and pinned over
  // the head of the plane, so a mark drawn under them breaks in two -- a stripe
  // on the ribbon and a stripe over the rows, with a blank band between.
  for (const p of L.guides) {
    if (p.t !== "topicband" && p.t !== "topicedge") continue;
    const y0 = Math.max(SY(p.top), 0);
    ctx.fillStyle = alphaMix(p.hue, p.t === "topicband" ? 7 : 55);
    ctx.fillRect(X(p.x), y0, p.t === "topicband" ? p.w : 1, viewH - y0);
  }

  // The strip stack's rail headers, last: opaque over the strips' own plane so
  // a mark panned to the left edge disappears under them, the same way the lane
  // rail occludes the rows.
  for (const p of L.strips) {
    if (p.t !== "striprow") continue;
    const y = SY(p.y);
    const x = railX;
    ctx.fillStyle = theme.bg;
    ctx.fillRect(x, y, railW, p.h);
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";
    // The strip's name is the sidebar's eyebrow (EYEBROW_CLASS); its summary
    // is the sidebar's control text.
    ctx.font = EYEBROW_FONT;
    ctx.letterSpacing = EYEBROW_TRACKING;
    ctx.fillStyle = theme.eyebrow;
    const label = p.label.toUpperCase();
    ctx.fillText(label, x + RAIL_PAD_X, y + p.h / 2);
    const lw = textWidth(ctx, label);
    ctx.letterSpacing = "0px";
    if (p.detail) {
      ctx.font = `11px ${SANS}`;
      ctx.fillStyle = theme.mutedSoft;
      // The run/stop control is markup pinned over the right of this cell, so
      // its width is reserved here: the summary clips before it, never under it.
      ctx.fillText(
        clipText(ctx, p.detail, railW - lw - 24 - (p.controls ? STRIP_CTRL_W : 0)),
        x + 14 + lw, y + p.h / 2,
      );
    }
    ctx.strokeStyle = theme.lineSoft;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x + railW - 0.5, y);
    ctx.lineTo(x + railW - 0.5, y + p.h);
    ctx.moveTo(x, y + p.h - 0.5);
    ctx.lineTo(x + railW, y + p.h - 0.5);
    ctx.stroke();
  }
  return { chipHits, guide: drawnGuide };
}

// --- component --------------------------------------------------------------

const LanesCanvasPlane = memo(function LanesCanvasPlane({
  scene,
  onPick = null,
  onToggleRow = null,
  onDrawTiming = null,
  onHover = null,
  onViewport = null,
  onFocusCell = null,
  onTurnWindow = null,
  onInsetPan = null,
  tooltipOf = null,
  className = "",
  // The plane's scrolling element, handed back so a host can keep owning zoom
  // anchoring, reveal and sticky measurement against the same element it did
  // when the plane was DOM.
  onScroller = null,
  // Surfaces that stay in DOM. The line is what SCALES: the canvas owns
  // anything that costs per lane per turn; a band or a ribbon whose size is
  // fixed regardless of how much the plane holds is cheaper left as markup,
  // and stays selectable and accessible for free. `children` sit in plane
  // coordinates and scroll both ways; `pinnedChildren` hold their vertical
  // place and track the axis horizontally, like the strip stack does.
  children = null,
  pinnedChildren = null,
  // Markup that floats ABOVE everything the plane draws and moves with it:
  // `floatingChildren` sit in plane coordinates over the rows, clipped to the
  // rows' viewport so they pass under the rail and the strip stack the way the
  // plane's own marks do; `floatingPinnedChildren` sit over the strip stack,
  // tracking the axis horizontally. Their offsets are written in the draw, as
  // the pinned layer's is, so a pan never re-renders them.
  floatingChildren = null,
  floatingPinnedChildren = null,
  // The cells a hovered strip mark stands for, [{ laneId, turn }]: when none of
  // their rows is on screen the plane points at the nearest.
  guide = null,
}) {
  const boxRef = useRef(null);
  const canvasRef = useRef(null);
  const scrollRef = useRef(null);
  const planeId = useId();
  const focusId = `${planeId}-focus`;
  const themeVersion = useThemeVersion();
  const themeRef = useRef(null);
  // Re-resolved by the DRAW, not by an effect. The draw is a layout effect, and
  // a passive effect that refreshed the palette ran AFTER it -- so the frame a
  // theme flip scheduled painted with the old colours and nothing scheduled
  // another. The plane kept the dark palette in light mode until an unrelated
  // scroll or resize happened to redraw it (found live).
  //
  // The version rides a ref assigned during render, so it is already current by
  // the time any effect runs and `draw` needs no dependency on it.
  const themeAtRef = useRef(-1);
  const themeVersionRef = useRef(themeVersion);
  themeVersionRef.current = themeVersion;
  const freshTheme = useCallback(() => {
    if (themeAtRef.current !== themeVersionRef.current) {
      themeRef.current = readTheme();
      themeAtRef.current = themeVersionRef.current;
    }
    return themeRef.current;
  }, []);

  const [size, setSize] = useState({ w: 0, h: 0 });
  const [hover, setHover] = useState(null); // { hit, clientX, clientY }
  // The hover, readable synchronously: several pointer moves can land inside one
  // frame, and the decision to notify the host has to see the previous one.
  const hoverRef = useRef(null);
  // The turn under the pointer. A ref, not state: the guides follow it across
  // every column, and re-rendering (let alone rebuilding the scene) per turn
  // crossed is the cost this plane exists to avoid.
  const pointerTurnRef = useRef(null);
  // The guide's targets resolved to this scene's rows, read by the draw.
  const guideRows = useMemo(() => (guide?.length && scene ? guideTargets(scene.rows, scene.tops, guide)
    .map((t) => ({ ...t, x: (scene.railW ?? RAIL_W) + t.turn * scene.cellW + scene.cellW / 2 })) : null), [guide, scene]);
  const guideRef = useRef(null);
  guideRef.current = guideRows;
  // Whether the plane holds keyboard focus, which is what decides if the focus
  // ring is drawn at all.
  const [focused, setFocused] = useState(false);
  // Keyboard focus is a position in the SCENE, not a DOM node: one offscreen
  // gridcell mirrors it for assistive technology (see the render below), which
  // is how a canvas plane stays reachable without minting an element per mark.
  const [focus, setFocus] = useState(null);
  // Latest-props ref keeps the draw and hit-test closures stable, so a pan does
  // not reattach listeners mid-gesture.
  const stateRef = useRef(null);
  stateRef.current = { scene, size, hover, focus, focused };

  const rafRef = useRef(0);
  const timerRef = useRef(0);
  const chipHitsRef = useRef([]);
  const lastWindowRef = useRef(null);
  const suppressClickRef = useRef(false);
  const pinnedRef = useRef(null);
  const floatRowsRef = useRef(null);
  const floatStripsRef = useRef(null);
  // Lets the draw ask for the next frame without depending on scheduleDraw,
  // which is defined in terms of the draw itself.
  const scheduleDrawRef = useRef(null);
  // The house scrollbars own the scrolling element, so the plane adopts THEIRS
  // rather than minting one: everything that reads a scroll position, hit-tests
  // a pointer or takes a key press binds to the OverlayScrollbars viewport.
  //
  // Held as STATE, not just a ref: every effect that binds to the scroller has
  // to re-run when it arrives, and it arrives a render after the plane mounts.
  // A ref alone leaves those effects bound to nothing forever (found live: the
  // native wheel listener silently never attached).
  const [scrollerEl, setScrollerEl] = useState(null);
  const onScrollerRef = useRef(onScroller);
  onScrollerRef.current = onScroller;
  const takeOsInstance = useCallback((inst) => {
    const viewport = inst ? inst.elements().viewport : null;
    if (viewport) {
      // The viewport is the library's element, so the plane stamps what it
      // needs onto it instead of declaring it in JSX. Everything here is what
      // the scrolling element must carry: the geometry contract's key, the grid
      // role a keyboard reader lands on, and a tab stop.
      viewport.setAttribute("data-canvas-plane-scroll", "");
      viewport.setAttribute("role", "grid");
      viewport.setAttribute("aria-label", "Lane timeline");
      viewport.tabIndex = 0;
      viewport.style.outline = "none";
    } else if (scrollRef.current) {
      scrollRef.current.removeAttribute("data-canvas-plane-scroll");
    }
    scrollRef.current = viewport;
    setScrollerEl(viewport);
    onScrollerRef.current?.(viewport);
  }, []);
  // The scroll event does not bubble, so it cannot ride the wrapper's handlers.
  useEffect(() => {
    if (!scrollerEl) return undefined;
    const onScroll = () => scheduleDrawRef.current?.();
    scrollerEl.addEventListener("scroll", onScroll, { passive: true });
    return () => scrollerEl.removeEventListener("scroll", onScroll);
  }, [scrollerEl]);
  useEffect(() => {
    if (!scrollerEl || !scene) return;
    PLANE_SCENES.set(scrollerEl, scene);
    scrollerEl.setAttribute("aria-rowcount", String(scene.rows?.length ?? 0));
    scrollerEl.setAttribute("aria-colcount", String(scene.turnCount ?? 0));
    if (scene.busy) scrollerEl.setAttribute("aria-busy", "true");
    else scrollerEl.removeAttribute("aria-busy");
  }, [scene, scrollerEl]);
  useEffect(() => {
    if (!scrollerEl) return;
    if (focus) scrollerEl.setAttribute("aria-activedescendant", focusId);
    else scrollerEl.removeAttribute("aria-activedescendant");
  }, [focus, focusId, scrollerEl]);
  // A live inset drag: { laneId, over, x, from, moved }. Declared with the other
  // refs so every handler that closes over it is below its binding.
  const dragRef = useRef(null);
  const draw = useCallback(() => {
    if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
    if (timerRef.current) window.clearTimeout(timerRef.current);
    rafRef.current = 0;
    timerRef.current = 0;
    const canvas = canvasRef.current;
    const scroller = scrollRef.current;
    const box = boxRef.current;
    const { scene: sc } = stateRef.current;
    if (!canvas || !scroller || !box || !sc) return;
    // The backing store is sized from the box's LIVE geometry, not from the
    // measured state: ResizeObserver delivery is suspended while the document
    // is hidden, so a resize that happens off-screen leaves the state stale and
    // a state-sized canvas draws one screen of content into the wrong box. The
    // state still drives WHEN to redraw; it never decides how big the surface
    // is. (The same reason the DOM plane pairs its observer with a window
    // listener — observers alone do not see a hidden document's resize.)
    const w = box.clientWidth;
    const h = box.clientHeight;
    if (!w || !h) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
    }
    const sz = { w, h };
    const t0 = performance.now();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const theme = freshTheme();
    ctx.fillStyle = theme.bg;
    ctx.fillRect(0, 0, sz.w, sz.h);
    const out = drawScene(ctx, sc, theme, {
      scrollX: scroller.scrollLeft, scrollY: scroller.scrollTop, viewW: sz.w, viewH: sz.h,
      focus: stateRef.current.focus, focused: stateRef.current.focused,
      sweep: sc.busy && !reducedMotion() ? ((t0 % SWEEP_MS) / SWEEP_MS) : null,
      pointerTurn: pointerTurnRef.current,
      guide: guideRef.current,
    });
    // The edge chips exist only in draw space, so the last frame's regions are
    // what a pointer or a key press has to hit-test against.
    chipHitsRef.current = out?.chipHits || [];
    PLANE_CHIPS.set(scroller, chipHitsRef.current);
    PLANE_GUIDES.set(scroller, out?.guide || null);
    // A windowed scene only holds strips for the band it was built for. When a
    // pan leaves that band the plane asks its caller for a wider one; the pad
    // means ordinary panning never triggers this.
    if (onTurnWindow) {
      const need = turnWindowFor({ scrollX: scroller.scrollLeft, viewW: w, cellW: sc.cellW, turnCount: sc.turnCount, pad: 0, railW: sc.railW });
      // `sc.turnWindow == null` also reports, so a caller that wants windowing
      // can start: an unwindowed scene covers everything, so a staleness test
      // alone would never fire and windowing would never begin. A host that
      // does NOT window simply passes no callback -- reporting a need it will
      // ignore costs a re-render per draw and never converges.
      if (need && (!sc.turnWindow || !windowCovers(sc.turnWindow, need))) {
        const next = turnWindowFor({ scrollX: scroller.scrollLeft, viewW: w, cellW: sc.cellW, turnCount: sc.turnCount, pad: 1, railW: sc.railW });
        const last = lastWindowRef.current;
        // Reporting an identical window would re-render, redraw and report
        // again; the guard is what keeps that from spinning.
        if (next && (!last || last.from !== next.from || last.to !== next.to)) {
          lastWindowRef.current = next;
          onTurnWindow(next);
        }
      }
    }
    // Keep the sweep moving while anything is pending, and stop the moment it
    // is not: an idle plane costs zero frames, which is the whole point of
    // drawing instead of reconciling.
    if (sc.busy && !reducedMotion()) scheduleDrawRef.current?.();
    if (pinnedRef.current) {
      pinnedRef.current.style.transform = `translateX(${-scroller.scrollLeft}px)`;
    }
    if (floatRowsRef.current) {
      floatRowsRef.current.style.transform = `translate(${-(scroller.scrollLeft + sc.railW)}px, ${-(scroller.scrollTop + sc.headerH)}px)`;
    }
    if (floatStripsRef.current) {
      floatStripsRef.current.style.transform = `translateX(${-(scroller.scrollLeft + sc.railW)}px)`;
    }
    onDrawTiming?.(performance.now() - t0);
  }, [onDrawTiming, onTurnWindow, freshTheme]);

  // rAF for frame-aligned draws, with a timeout backstop: rAF is starved in a
  // hidden or display-throttled document, and a one-shot latch with no second
  // path leaves the canvas at its 300x150 default until the next event AFTER
  // the document becomes visible again (the same trap the DOM plane's scroll
  // reader hit, and the reason it carries the same pair).
  const scheduleDraw = useCallback(() => {
    if (rafRef.current || timerRef.current) return;
    rafRef.current = window.requestAnimationFrame(draw);
    timerRef.current = window.setTimeout(draw, 64);
  }, [draw]);
  scheduleDrawRef.current = scheduleDraw;

  useEffect(() => () => {
    if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
    if (timerRef.current) window.clearTimeout(timerRef.current);
  }, []);

  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return undefined;
    const measure = () => setSize((cur) => (
      cur.w === box.clientWidth && cur.h === box.clientHeight
        ? cur
        : { w: box.clientWidth, h: box.clientHeight }
    ));
    measure();
    // The observer catches container-driven resizes (a panel expanding); the
    // window listener catches window-driven ones even while the document is
    // hidden, where observer delivery is suspended.
    const stop = observeSettledSize(box, measure);
    window.addEventListener("resize", measure);
    return () => {
      stop();
      window.removeEventListener("resize", measure);
    };
  }, []);

  // The plane owns the one measurement of its own box and reports it up. A
  // caller that measures the container itself ends up with a second, stale copy
  // — the fit zoom and the inset box width were computed from a width nothing
  // re-read after a resize.
  useEffect(() => {
    if (size.w && size.h) onViewport?.(size);
  }, [size, onViewport]);

  // What the plane HOLDS is painted in the commit that changed it, so the
  // surface is never blank waiting for a frame that a hidden document or a busy
  // main thread may not deliver.
  useLayoutEffect(() => { draw(); }, [scene, size, themeVersion, draw]);
  // What the reader is DOING -- hover, focus -- coalesces into one rAF instead:
  // those change per pointer move, and a synchronous draw per move would tie
  // the pointer to the draw cost.
  useEffect(() => { scheduleDraw(); }, [hover, focus, focused, guideRows, scheduleDraw]);

  // A focus that outlives the row it pointed at would draw a ring on whatever
  // took its place, so it is dropped when the scene no longer charts it.
  useEffect(() => {
    if (focus && !scene?.index?.rail?.some((r) => r.laneId === focus.laneId)) setFocus(null);
  }, [scene, focus]);

  useEffect(() => { onFocusCell?.(focus); }, [focus, onFocusCell]);


  const hitAt = useCallback((e) => {
    const scroller = scrollRef.current;
    const { scene: sc } = stateRef.current;
    if (!scroller || !sc) return null;
    const r = scroller.getBoundingClientRect();
    const vx = e.clientX - r.left;
    const vy = e.clientY - r.top;
    // Edge chips are pinned over the plane, in viewport coordinates, so they
    // answer first — and from the last frame's regions, which is the only place
    // they exist.
    for (const c of chipHitsRef.current) {
      if (vx >= c.x0 && vx <= c.x1 && vy >= c.y0 && vy <= c.y1) {
        return c.related
          ? { type: "relatedchip", laneId: c.laneId, rowTop: c.rowTop, label: c.label, dir: c.dir }
          : { type: "edgechip", laneId: c.laneId, turn: c.turn, label: c.label };
      }
    }
    // A pinned telemetry band is drawn away from its place in the flow, so a
    // pointer inside its DRAWN box has to be asked about at its flow position
    // -- otherwise the band answers for whatever rows scrolled under it.
    const sm = sc.systemMeta;
    const shift = systemBandShift(sm, {
      scrollY: scroller.scrollTop, viewH: stateRef.current.size.h,
    });
    if (shift < 0) {
      const drawnTop = sm.top - scroller.scrollTop + shift;
      if (vy >= drawnTop && vy < drawnTop + sm.h) {
        return hitTestScene(sc, vx + scroller.scrollLeft, sm.top + (vy - drawnTop), vy);
      }
    }
    // The strip stack is pinned to the viewport, so it answers on the RAW
    // viewport y while the plane answers on the scrolled one.
    return hitTestScene(sc, vx + scroller.scrollLeft, vy + scroller.scrollTop, vy);
  }, []);

  // Bring the focused cell into view. A focus that has scrolled off is not a
  // focus a keyboard reader can use, and the ring is drawn in plane space.
  const revealFocus = useCallback((next) => {
    const scroller = scrollRef.current;
    const { scene: sc, size: sz } = stateRef.current;
    if (!scroller || !sc || !next) return;
    const r = focusRect(sc, next);
    if (!r) return;
    const padX = 40;
    const padY = ROW_H;
    // The rail and the strip stack cover the plane's left and top edges, so
    // "in view" means clear of them, not clear of the viewport.
    const railW = sc.railW ?? RAIL_W;
    const left = scroller.scrollLeft + railW + padX;
    const right = scroller.scrollLeft + sz.w - padX;
    if (r.x0 < left) scroller.scrollLeft = Math.max(0, r.x0 - railW - padX);
    else if (r.x1 > right) scroller.scrollLeft = r.x1 - sz.w + padX;
    const top = scroller.scrollTop + sc.headerH + padY;
    const bottom = scroller.scrollTop + sz.h - padY;
    if (r.y0 < top) scroller.scrollTop = Math.max(0, r.y0 - sc.headerH - padY);
    else if (r.y1 > bottom) scroller.scrollTop = r.y1 - sz.h + padY;
  }, []);

  // A topic bucket and a system bucket are identified by their RANGE, not by a
  // turn: without `start` here, crossing from one bucket to the next read as the
  // same hit and the host was never told -- the card followed the pointer (it
  // reads this component's own state) while everything driven by the host stayed
  // on the bucket first entered.
  const sameHit = (a, b) => a?.type === b?.type && a?.laneId === b?.laneId
    && a?.turn === b?.turn && a?.rowId === b?.rowId && a?.strip === b?.strip
    && a?.start === b?.start && a?.kind === b?.kind;

  const onPointerMove = useCallback((e) => {
    const st = dragRef.current;
    if (st) {
      const dx = e.clientX - st.x;
      if (!st.moved && Math.abs(dx) < 3) return;
      st.moved = true;
      // Drag the CONTENT: moving the pointer left pulls later turns into view,
      // which is the direction a hand expects from grabbing a surface.
      onInsetPan(st.laneId, st.from - dx, st.over);
      return;
    }
    // The turn the pointer is over, whatever is drawn there: the guides answer
    // "where am I in this session" and that question does not depend on
    // something having happened at the position being pointed at.
    const scroller = scrollRef.current;
    const { scene: sc } = stateRef.current;
    if (scroller && sc) {
      const r = scroller.getBoundingClientRect();
      const px = e.clientX - r.left;
      const railW = sc.railW ?? RAIL_W;
      const turn = px < railW
        ? null
        : Math.floor((px - railW + scroller.scrollLeft) / sc.cellW);
      const next = turn != null && turn >= 0 && turn < sc.turnCount ? turn : null;
      if (next !== pointerTurnRef.current) {
        pointerTurnRef.current = next;
        // Published on the scroller, the way the measured size is: the guides
        // are drawn from it, so anything that needs to know where the pointer
        // is -- an overlay, a test -- can read it without a frame having run.
        if (next == null) scroller.removeAttribute("data-pointer-turn");
        else scroller.setAttribute("data-pointer-turn", String(next));
        scheduleDraw();
      }
    }
    const hit = hitAt(e);
    // The hover is decided here rather than inside a state updater. React runs
    // updaters during the RENDER phase, so telling the host from in there means
    // updating it while this component is rendering -- and the host's hover
    // state then lands a render late, or not at all.
    const cur = hoverRef.current;
    if (!hit) {
      if (!cur) return;
      hoverRef.current = null;
      setHover(null);
      onHover?.(null);
      return;
    }
    // One tooltip driven by a hit-test, replacing one HoverTooltip per mark:
    // the state only churns when the pointer crosses into a different object.
    if (cur && sameHit(cur.hit, hit)) {
      if (cur.clientX === e.clientX && cur.clientY === e.clientY) return;
      const moved = { ...cur, clientX: e.clientX, clientY: e.clientY };
      hoverRef.current = moved;
      setHover(moved);
      return;
    }
    const next = { hit, clientX: e.clientX, clientY: e.clientY };
    hoverRef.current = next;
    setHover(next);
    onHover?.(hit);
  }, [hitAt, onHover, onInsetPan, scheduleDraw]);

  // Bound NATIVELY, with { passive: false }: React registers wheel at the root
  // as a passive listener, so `preventDefault` inside an onWheel prop is inert —
  // the child would pan AND the parent would scroll under it.
  //
  // A PLAIN wheel is never taken. It cannot be: a trackpad emits a stream of
  // diagonal deltas, so a scroll that is obviously vertical to the hand still
  // carries small non-zero deltaX, and any per-event "is this horizontal?" test
  // fires on the frames where jitter tips the balance. The result is a scroll
  // down the plane that shoves the child sideways every few frames (found live,
  // and my own single-axis synthetic events were too clean to catch it).
  //
  // So the plane keeps the wheel and the child gets an explicit gesture:
  // shift+wheel, which already means "horizontal" everywhere else. Nothing the
  // hand means as vertical can reach the child at all.
  const onWheel = useCallback((e) => {
    if (!e.shiftKey) return;
    const { scene: sc } = stateRef.current;
    if (!sc || !onInsetPan) return;
    // Shift+wheel reports on whichever axis the device uses for it.
    const dx = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
    if (!dx) return;
    const hit = hitAt(e);
    const laneId = hit?.insetOf || (hit?.type === "inset" ? hit.laneId : null);
    if (!laneId) return;
    const box = sc.index.insets.find((b) => b.laneId === laneId);
    const over = insetPanRange(box);
    // A child that fits has nothing to pan, so the gesture stays the parent's.
    if (over <= 0) return;
    const at = box.scene?.meta?.zoom?.panX || 0;
    // At the end of its travel the child hands the gesture back instead of
    // swallowing it, which is the scroll chaining a nested region does.
    if ((dx > 0 && at >= over) || (dx < 0 && at <= 0)) return;
    e.preventDefault();
    onInsetPan(laneId, at + dx, over);
  }, [hitAt, onInsetPan]);

  // Drag to pan a zoomed child, which is the gesture the box actually affords:
  // direct manipulation inside a region that visibly overflows, and one that
  // cannot be confused with scrolling the page because it starts from a press.
  // A press that never moves stays a click, so picking a cell still works.
  const onPointerDown = useCallback((e) => {
    const { scene: sc } = stateRef.current;
    if (!sc || !onInsetPan || e.button !== 0) return;
    const hit = hitAt(e);
    const laneId = hit?.insetOf || (hit?.type === "inset" ? hit.laneId : null);
    if (!laneId) return;
    const box = sc.index.insets.find((b) => b.laneId === laneId);
    const over = insetPanRange(box);
    if (over <= 0) return;
    dragRef.current = {
      laneId, over, x: e.clientX, from: box.scene?.meta?.zoom?.panX || 0, moved: false,
    };
  }, [hitAt, onInsetPan]);

  const onPointerUp = useCallback(() => {
    const st = dragRef.current;
    dragRef.current = null;
    // A drag that moved consumed the press; the click that follows it is not a
    // pick, so it is swallowed once.
    if (st?.moved) suppressClickRef.current = true;
  }, []);

  useEffect(() => {
    if (!scrollerEl) return undefined;
    scrollerEl.addEventListener("wheel", onWheel, { passive: false });
    return () => scrollerEl.removeEventListener("wheel", onWheel);
  }, [onWheel, scrollerEl]);

  const onClick = useCallback((e) => {
    if (suppressClickRef.current) { suppressClickRef.current = false; return; }
    const hit = hitAt(e);
    if (!hit) return;
    if (hit.type === "rail" && (hit.rowKind === "head" || hit.expandable)) { onToggleRow?.(hit); return; }
    // Pointer and keyboard share one focus, so the ring always sits on the cell
    // the reader last addressed, whichever way they addressed it.
    if (hit.laneId && hit.turn != null && !hit.insetOf) {
      const next = { laneId: hit.laneId, turn: hit.turn };
      setFocus(next);
      if (hit.type === "edgechip") revealFocus(next);
    }
    onPick?.(hit);
  }, [hitAt, onPick, onToggleRow, revealFocus]);

  // Arrow keys move focus along a lane's marks and between lanes, the way a
  // grid moves: panning is the scrollbar's job. Space/Enter opens the focused
  // cell, so everything the pointer can reach a keyboard can too.
  const onKeyDown = useCallback((e) => {
    const scroller = scrollRef.current;
    const { scene: sc, focus: cur } = stateRef.current;
    if (!scroller || !sc) return;
    const DIR = {
      ArrowRight: "right", ArrowLeft: "left", ArrowDown: "down", ArrowUp: "up",
      Home: "rowStart", End: "rowEnd",
    };
    const dir = DIR[e.key];
    if (dir) {
      e.preventDefault();
      const next = moveFocus(sc, cur, dir);
      setFocus(next);
      revealFocus(next);
      return;
    }
    if (e.key === "Enter" || e.key === " ") {
      if (!cur) return;
      e.preventDefault();
      onPick?.({ type: "cell", laneId: cur.laneId, turn: cur.turn });
      return;
    }
    if (e.key === "Escape") {
      setFocus(null);
      return;
    }
    // Paging still pans, because a page is a viewport fact rather than a
    // position in the model.
    if (e.key === "PageDown") { scroller.scrollTop += stateRef.current.size.h - sc.headerH; e.preventDefault(); }
    else if (e.key === "PageUp") { scroller.scrollTop -= stateRef.current.size.h - sc.headerH; e.preventDefault(); }
  }, [onPick, revealFocus]);

  const overPannable = (() => {
    const laneId = hover?.hit?.insetOf || (hover?.hit?.type === "inset" ? hover.hit.laneId : null);
    if (!laneId) return false;
    return insetPanRange(scene?.index?.insets?.find((b) => b.laneId === laneId)) > 0;
  })();
  const panCursor = overPannable ? "grab" : undefined;

  const focusLabel = focus ? describeFocus(scene, focus) : "";
  // 1-based, and it is the row's place among the rows a reader can reach — the
  // number a screen reader announces has to match what Up and Down do.
  const focusRowIndex = focus
    ? (scene?.index?.rail?.findIndex((r) => r.laneId === focus.laneId) ?? -1) + 1
    : 1;

  return (
    <div ref={boxRef} data-canvas-view={`${size.w}x${size.h}`} className={`relative min-h-0 min-w-0 flex-1 overflow-hidden ${className}`}>
      <canvas
        ref={canvasRef}
        aria-hidden
        className="absolute inset-0 block"
        style={{ width: "100%", height: "100%" }}
      />
      {/* The scroller carries only a spacer: real scrolling over a canvas that
          never grows past one screen. The house scrollbars own the scrolling
          element, so the plane's own handlers ride this wrapper -- every one of
          them bubbles up from the viewport -- and the attributes the scrolling
          element must carry are stamped on the viewport itself. */}
      <div
        className="absolute inset-0"
        style={{ cursor: panCursor }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onPointerMove={onPointerMove}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerLeave={() => {
          hoverRef.current = null;
          pointerTurnRef.current = null;
          scrollRef.current?.removeAttribute?.("data-pointer-turn");
          setHover(null);
          onHover?.(null);
          dragRef.current = null;
          scheduleDraw();
        }}
        onClick={onClick}
        onKeyDown={onKeyDown}
      >
        <ScrollArea className="h-full w-full" instanceRef={takeOsInstance} observeMutations={false}>
          <div style={{ width: (scene?.railW ?? RAIL_W) + (scene?.width ?? 0), height: scene?.height ?? 0 }} />
          {children}
          {/* The one accessible node the plane needs: a gridcell mirroring the
              keyboard focus, named by the scene. A canvas cannot hold focusable
              children, and minting one element per mark is exactly the cost this
              renderer exists to avoid — so `aria-activedescendant` points here and
              this row's label is updated as focus moves. Positioned off-screen
              rather than display:none, which would take it out of the a11y tree. */}
          <div role="row" aria-rowindex={focusRowIndex} style={SR_ONLY}>
            <span id={focusId} role="gridcell" aria-selected={focus != null}>{focusLabel}</span>
          </div>
        </ScrollArea>
      </div>
      {/* Pinned DOM, over the canvas and under the tooltip. Its horizontal
          offset is written in the draw rather than through state: a pinned
          layer that re-rendered on every scroll tick would reintroduce exactly
          the per-frame reconciliation the canvas plane exists to remove. */}
      {pinnedChildren ? (
        <div
          ref={pinnedRef}
          data-canvas-pinned=""
          className="pointer-events-none absolute left-0 top-0 will-change-transform"
          style={{ width: (scene?.railW ?? RAIL_W) + (scene?.width ?? 0) }}
        >
          {pinnedChildren}
        </div>
      ) : null}
      {floatingChildren ? (
        <div
          data-canvas-floating="rows"
          className="pointer-events-none absolute bottom-0 right-0 overflow-hidden"
          style={{ left: scene?.railW ?? RAIL_W, top: scene?.headerH ?? 0 }}
        >
          <div ref={floatRowsRef} className="absolute left-0 top-0 will-change-transform">
            {floatingChildren}
          </div>
        </div>
      ) : null}
      {floatingPinnedChildren ? (
        <div
          data-canvas-floating="strips"
          className="pointer-events-none absolute right-0 top-0 overflow-hidden"
          style={{ left: scene?.railW ?? RAIL_W, height: scene?.headerH ?? 0 }}
        >
          <div ref={floatStripsRef} className="absolute left-0 top-0 will-change-transform">
            {floatingPinnedChildren}
          </div>
        </div>
      ) : null}
      {hover && tooltipOf ? (
        <PlaneTip tooltipOf={tooltipOf} hit={hover.hit} clientX={hover.clientX} clientY={hover.clientY} />
      ) : null}
    </div>
  );
});

export default LanesCanvasPlane;
export { readTheme, parseColor, alphaMix, overMix, markColor, drawScene, roundRect, clipText, textWidth, drawChevron, SANS as CANVAS_SANS };
